/*
 * app.js — the /gpx-viewer page: open any GPS file (or a whole Strava/Garmin
 * export), see it on the map and on a profile, read honest numbers, fix what
 * is wrong with it, and save it in the format the next app wants.
 *
 * Modules (all in public/js/gps-track/): worker.js reads files off the main
 * thread; parse / geo / analysis / write / edit are pure; map-layer, terrain
 * and chart draw; dem fetches terrain; image renders the picture and the
 * printable report.
 *
 * State is ONE Doc (all open files combined) plus an undo stack of Docs. Every
 * edit is GpsEdit.* → new Doc; derived numbers are cached per track object, so
 * an undo is free and nothing is recomputed that did not change.
 *
 * Layout (25.09.2026): one frame — bar · rail (summary, tabs, save) · map ·
 * resizable profile. See the comment at the top of the blade's <style>.
 */
(function () {
    'use strict';
    var CFG = window.GT_CFG || {};
    var SLUG = 'gpx-viewer';
    var $ = function (id) { return document.getElementById(id); };
    var S = {};
    try { S = JSON.parse(($('gtStrings') || {}).textContent || '{}'); } catch (_) {}
    function t(k, vars) {
        var s = S[k] != null ? String(S[k]) : k;
        if (vars) for (var v in vars) s = s.split(':' + v).join(vars[v]);
        return s;
    }
    var LOC = CFG.locale || document.documentElement.lang || 'en';
    var RTL = CFG.dir === 'rtl';
    var NF1 = new Intl.NumberFormat(LOC, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
    var NF0 = new Intl.NumberFormat(LOC, { maximumFractionDigits: 0 });
    var NF2 = new Intl.NumberFormat(LOC, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    var PALETTE = ['#3e7bfa', '#e5484d', '#30a46c', '#f76b15', '#8e4ec6', '#12a594', '#d6409f', '#b08800', '#0090ff', '#e54666'];
    var CAT_COLOR = { HC: '#b4232c', '1': '#c92a2a', '2': '#d9480f', '3': '#e8590c', '4': '#c77c02' };

    var G = window.GpsGeo, E = window.GpsEdit, W = window.GpsWrite, A = window.GpsAnalysis;

    function ae(ev, data) { try { if (window.__ae) window.__ae(ev, Object.assign({ slug: SLUG }, data || {})); } catch (_) {} }
    function stage(s, d) { try { if (window.__stage) window.__stage(s, d); } catch (_) {} }
    function toast(m, ty) { if (window.__toast) window.__toast(m, ty || 'info'); }

    // ── settings ──────────────────────────────────────────────────────────
    function load(k, d) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (_) { return d; } }
    function save(k, v) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (_) {} }
    // miles only where both the page and the browser speak American English
    var imperialDefault = LOC === 'en' && /^en-(US|LR)$|^my/.test(navigator.language || '');
    var units = load('units', imperialDefault ? 'imperial' : 'metric');
    var xKind = load('xkind', 'dist');
    var colorBy = load('colorby', 'file');
    var tab = load('tab', 'stats');
    var part = load('part', '');
    var shadeOn = load('shade', '1') === '1';
    var baseMode = load('base', '');          // '' = follow the shared map setting; 'sat' = satellite here
    var reliefOn = load('relief', '0') === '1';
    var marksOn = load('cmarks', '1') === '1';
    var profH = +load('profh', 0) || 0;       // 0 = the layout's default
    var timeMode = load('addmode', '');        // '' = guess from the track, 'foot' | 'bike'
    var hiddenPanels = {};
    try { hiddenPanels = JSON.parse(load('hidden', '{"grade":1,"temp":1,"cad":1}')) || {}; } catch (_) { hiddenPanels = { grade: 1, temp: 1, cad: 1 }; }

    // ── units ─────────────────────────────────────────────────────────────
    var U = {
        dist: function (m) {
            if (m == null || isNaN(m)) return '—';
            if (units === 'imperial') {
                var mi = m / 1609.344;
                if (mi < 0.1) return NF0.format(m / 0.3048) + ' ' + t('u_ft');
                return (mi < 100 ? NF2.format(mi) : NF0.format(mi)) + ' ' + t('u_mi');
            }
            if (m < 1000) return NF0.format(m) + ' ' + t('u_m');
            return (m < 100000 ? NF2.format(m / 1000) : NF0.format(m / 1000)) + ' ' + t('u_km');
        },
        ele: function (m) {
            if (m == null || isNaN(m)) return '—';
            return units === 'imperial' ? NF0.format(m / 0.3048) + ' ' + t('u_ft') : NF0.format(m) + ' ' + t('u_m');
        },
        speed: function (ms) {
            if (ms == null || isNaN(ms)) return '—';
            return units === 'imperial' ? NF1.format(ms * 2.2369363) + ' ' + t('u_mph') : NF1.format(ms * 3.6) + ' ' + t('u_kmh');
        },
        pace: function (ms) {
            if (!ms || isNaN(ms) || ms < 0.3) return '—';
            var s = (units === 'imperial' ? 1609.344 : 1000) / ms;
            var mm = Math.floor(s / 60), ss = Math.round(s % 60);
            if (ss === 60) { mm++; ss = 0; }
            return mm + ':' + (ss < 10 ? '0' : '') + ss + ' ' + (units === 'imperial' ? t('u_per_mi') : t('u_per_km'));
        },
        dur: function (s) {
            if (s == null || isNaN(s)) return '—';
            s = Math.round(s);
            var h = Math.floor(s / 3600), m = Math.floor(s % 3600 / 60), ss = s % 60;
            var d = Math.floor(h / 24);
            if (d >= 1) return t('dur_days', { d: d, h: h % 24 });
            return (h ? h + ':' + (m < 10 ? '0' : '') : '') + m + ':' + (ss < 10 ? '0' : '') + ss;
        },
        /* An estimate is not a stopwatch reading: whole minutes, in words
         * (Intl.DurationFormat where the browser has it, our pattern if not). */
        durShort: function (s) {
            if (s == null || isNaN(s)) return '—';
            var m = Math.max(1, Math.round(s / 60)), h = Math.floor(m / 60); m = m % 60;
            try { if (window.Intl && Intl.DurationFormat) return new Intl.DurationFormat(LOC, { style: 'narrow' }).format(h ? { hours: h, minutes: m } : { minutes: m }); } catch (_) {}
            return h ? t('dur_hm', { h: h, m: m }) : t('dur_m', { m: m });
        },
        unitLen: function () { return units === 'imperial' ? 1609.344 : 1000; },
        eleK: function () { return units === 'imperial' ? 1 / 0.3048 : 1; },
        spdK: function () { return units === 'imperial' ? 2.2369363 : 3.6; }
    };

    // ── state ─────────────────────────────────────────────────────────────
    var doc = null, undo = [], redo = [];
    var active = 0;            // active track index
    var selection = null;      // {from, to} on active track
    var cursorIdx = null;      // on active track
    var srcFiles = {};         // key → {name, size}
    var cache = new WeakMap();
    var map = null, layer = null, chart = null;
    var worker = null, workerReady = false, reqId = 0, pending = {};
    var archive = null;        // {id, listing}
    var heatOn = false, heatLines = [];

    function derived(trk) {
        var d = cache.get(trk);
        if (d) return d;
        var sr = G.series(trk);
        d = { sr: sr };
        d.speed = G.speed(sr);
        d.grade = sr.ele ? G.grade(sr, 60) : null;
        d.gradeS = null; // the smoother grade the profile is coloured with, when first asked
        d.stats = G.stats(sr, { speed: d.speed });
        d.foot = isFoot(trk, d.stats);
        d.spikes = G.spikes(sr);
        cache.set(trk, d);
        return d;
    }
    // analysis is computed when first asked for (the parts tab, chart marks)
    function climbsOf(tr) { var d = derived(tr); if (!d.climbs) d.climbs = A ? A.climbs(d.sr) : []; return d.climbs; }
    function bestOf(tr) { var d = derived(tr); if (!d.best) d.best = A ? A.bestEfforts(d.sr, d.foot ? A.EFFORTS_FOOT : A.EFFORTS_RIDE) : []; return d.best; }
    function powerOf(tr) { var d = derived(tr); if (d.power === undefined) d.power = A ? A.powerCurve(d.sr) : null; return d.power; }
    /* A route without time: how long it takes on foot and by bike, at the
     * flat speeds the person set (5 and 20 km/h until they change them). */
    function estOf(tr, mode) {
        var d = derived(tr);
        var sp = mode === 'bike' ? spd('bike') : spd('foot');
        var k = mode + '@' + sp;
        d.est = d.est || {};
        if (!d.est[k]) d.est[k] = A && A.estimate ? A.estimate(d.sr, { mode: mode, speed: sp }) : null;
        return d.est[k];
    }
    function spd(mode) { var v = parseFloat(load('spd_' + mode, mode === 'bike' ? '20' : '5')); if (!(v > 0.5)) v = mode === 'bike' ? 20 : 5; return units === 'imperial' ? v * 0.44704 : v / 3.6; }
    function guessMode(tr, d) {
        if (timeMode === 'foot' || timeMode === 'bike') return timeMode;
        var s = String(tr.sport || '').toLowerCase();
        if (/cycl|bik|ride/.test(s)) return 'bike';
        if (/run|walk|hik|trek|trail|foot/.test(s)) return 'foot';
        return d.stats.distance > 40000 ? 'bike' : 'foot';
    }
    function steepOf(tr) { var d = derived(tr); if (d.steep === undefined) d.steep = A ? A.gradeExtremes(d.sr) : null; return d.steep; }
    function shadeOf(tr) { var d = derived(tr); if (!d.gradeS && d.sr.ele) d.gradeS = A && A.gradeBands ? A.gradeBands(d.sr) : G.grade(d.sr, 200); return d.gradeS; }
    /* Elevation gain to SHOW. An untouched activity from a watch carries the
     * gain the watch computed from its full-rate barometer — the number Garmin
     * Connect and Strava show for that file — and it is shown as is. Our own
     * count (checked against those same totals: ~5 % apart) is used once the
     * track has been edited, for a selection, and for files without totals. */
    function gainOf(tr, d) {
        var c = d.stats.climb || {};
        if (tr.device && !tr.edited && tr.device.ascent != null && d.sr.ele) {
            return { gain: tr.device.ascent, loss: tr.device.descent != null ? tr.device.descent : c.loss, src: 'device' };
        }
        return { gain: c.gain, loss: c.loss, src: c.source };
    }
    function isFoot(trk, st) {
        var s = String(trk.sport || '').toLowerCase();
        if (/run|walk|hik|trek|trail|foot|mountaineer|snowshoe|бег|ход|поход/.test(s)) return true;
        if (/cycl|bik|ride|ski|drive|flight|flying|glid|sail|boat|kayak|paddl|row|swim/.test(s)) return false;
        var v = st.movingSpeed || st.avgSpeed;
        return v != null && v > 0.3 && v < 2.8; // under ~10 km/h moving: on foot
    }

    // ── worker ────────────────────────────────────────────────────────────
    function getWorker() {
        if (worker) return worker;
        worker = new Worker(CFG.workerUrl);
        worker.onmessage = function (e) {
            var m = e.data || {};
            if (m.kind === 'pong') { workerReady = true; return; }
            if (m.kind === 'heat' || m.kind === 'heat-done') { onHeat(m); return; }
            var p = pending[m.id];
            if (!p) return;
            delete pending[m.id];
            if (m.kind === 'error') p.reject(new Error(m.error)); else p.resolve(m);
        };
        worker.onerror = function (e) {
            ae('tool_diagnostic', { issue: 'gt_worker_error', msg: String(e.message || '').slice(0, 120) });
            for (var k in pending) { pending[k].reject(new Error('worker-failed')); delete pending[k]; }
        };
        try { worker.postMessage({ op: 'ping', id: 0 }); } catch (_) {}
        return worker;
    }
    function call(msg) {
        return new Promise(function (resolve, reject) {
            var id = ++reqId;
            msg.id = id;
            pending[id] = { resolve: resolve, reject: reject };
            try { getWorker().postMessage(msg); } catch (e) { delete pending[id]; reject(e); }
        });
    }

    // ── loading files ─────────────────────────────────────────────────────
    function fileKey(f) { return (f.name || 'file') + '|' + (f.size || 0) + '|' + (f.lastModified || 0); }

    function errText(msg) {
        var m = String(msg || '');
        if (/unknown-format/.test(m)) return t('err_format');
        if (/kmz-no-kml/.test(m)) return t('err_kmz');
        if (/zip-/.test(m)) return t('err_zip');
        if (/csv-no-coords/.test(m)) return t('err_csv');
        if (/not-fit/.test(m)) return t('err_fit');
        if (/fflate/.test(m)) return t('err_generic');
        if (/Unexpected|JSON/.test(m)) return t('err_json');
        return t('err_generic');
    }

    /* The worker is the right place for a big file, but its scripts arrive over
     * the network: on a cold cache (first visit after a deploy, a slow line) it
     * can take seconds to start. An ordinary track is parsed in milliseconds,
     * and every parser is already on the page — so until the worker answers,
     * a file up to 25 MB is read right here. Archives always go to the worker:
     * only it holds the zip reader. The worker is warmed the moment a person
     * reaches for a file (hover, drag, focus), not when they have picked one. */
    var LOCAL_MAX = 25 * 1024 * 1024;
    function warmWorker() { try { getWorker(); } catch (_) {} loadTz(); }
    function parseLocal(f) {
        return f.arrayBuffer().then(function (ab) { return { kind: 'doc', doc: GpsParse.bytesToDoc(new Uint8Array(ab), f.name) }; });
    }
    function openOne(f) {
        return f.slice(0, 8).arrayBuffer().then(function (ab) {
            var h = new Uint8Array(ab);
            var archiveLike = GpsParse.isZip(h) && !/.kmz$/i.test(f.name || '');
            if (!archiveLike && !workerReady && f.size <= LOCAL_MAX) return parseLocal(f).then(function (r) { r.lane = 'page'; return r; });
            return call({ op: 'open', file: f }).then(function (r) { r.lane = 'worker'; return r; });
        });
    }

    var loading = 0;
    function setBusy(on, text) {
        loading += on ? 1 : -1;
        var b = $('gtBusy');
        if (!b) return;
        b.hidden = loading <= 0;
        if (text) $('gtBusyText').textContent = text;
    }

    var openSource = 'pick';
    function openFiles(files, mode) {
        files = Array.prototype.slice.call(files || []).filter(Boolean);
        if (!files.length) return;
        if (mode !== 'add') { resetAll(); }
        var src = openSource; openSource = 'pick';
        if (src === 'draw') { Draw.begin(files[0]); return; }
        stage('parsing', { n: files.length });
        setBusy(true, t('busy_reading'));
        var docs = [], errors = [], opened = [];
        var chain = Promise.resolve();
        files.forEach(function (f) {
            chain = chain.then(function () {
                var t0 = performance.now();
                return openOne(f).then(function (res) {
                    if (res.kind === 'archive') { showArchive(res, f); return; }
                    tagDoc(res.doc, f);
                    docs.push(res.doc);
                    opened.push({ f: f, doc: res.doc });
                    ae('gt_open', { fmt: res.doc.format, pts: countPts(res.doc), ms: Math.round(performance.now() - t0), mb: Math.round(f.size / 1e5) / 10, w: res.doc.warnings.length, lane: res.lane, src: src });
                }).catch(function (e) {
                    errors.push({ name: f.name, err: e.message });
                    ae('tool_diagnostic', { issue: 'gt_parse_failed', ext: (String(f.name).match(/\.[a-z0-9]+$/i) || [''])[0], err: String(e.message).slice(0, 80) });
                });
            });
        });
        return chain.then(function () {
            setBusy(false);
            if (docs.length) addDocs(docs, mode === 'add');
            // A replacing open dropped the previous document before reading (resetAll above).
            // When the new files gave nothing to show — unreadable, empty, or an archive whose
            // list is still to be picked from — the workspace goes too: left on screen it shows
            // the old track over doc = null, and every control in it acts on nothing (Sentry
            // TIMBRICA-FRONTEND-P6: «c is null» from the simplify slider, Firefox 115).
            if (!doc) hideWork();
            if (errors.length) showErrors(errors);
            if (src !== 'sample') opened.forEach(function (o) { Recent.add(o.f, o.doc); });
        });
    }
    function tagDoc(d, f) {
        var key = f ? fileKey(f) : 'sample';
        srcFiles[key] = { name: f ? f.name : 'sample', size: f ? f.size : 0 };
        d.tracks.forEach(function (tr) { tr.src = key; tr.srcName = f ? f.name : null; if (!tr.name) tr.name = d.name || (f ? f.name.replace(/\.[^.]+(\.gz)?$/i, '') : null); });
        d.waypoints.forEach(function (w) { w.src = key; });
    }
    function countPts(d) { return d.tracks.reduce(function (a, tr) { return a + tr.segs.reduce(function (b, s) { return b + s.n; }, 0); }, 0); }

    function addDocs(docs, append) {
        var all = append && doc ? [doc].concat(docs) : docs;
        var combined = E.combine(all);
        combined.warnings = [];
        all.forEach(function (d) { (d.warnings || []).forEach(function (w) { combined.warnings.push(w); }); });
        combined.networkLink = all.some(function (d) { return d.networkLink; });
        combined.device = docs.length === 1 && !append ? docs[0].device : (doc && doc.device) || null;
        combined.name = (doc && doc.name) || docs[0].name || null;
        combined.tracks.forEach(function (tr, i) { if (!tr.color) tr.color = PALETTE[i % PALETTE.length]; });
        if (!combined.tracks.length && !combined.waypoints.length && !(combined.areas || []).length) {
            showErrors([{ name: '', err: combined.networkLink ? 'networklink' : 'empty' }]);
            if (!doc) return;
        }
        commit(combined, null, true);
        // active: the longest track
        var best = 0, bn = -1;
        doc.tracks.forEach(function (tr, i) { var n = tr.segs.reduce(function (a, s) { return a + s.n; }, 0); if (n > bn) { bn = n; best = i; } });
        active = best;
        showWork();
        fitAll();
        stage('loaded', { tracks: doc.tracks.length, wpt: doc.waypoints.length });
    }

    function showErrors(list) {
        var box = $('gtErrors');
        if (!box) return;
        box.innerHTML = '';
        list.forEach(function (e) {
            var p = document.createElement('p');
            p.className = 'tc-msg bad';
            var msg = e.err === 'networklink' ? t('err_networklink') : e.err === 'empty' ? t('err_empty') : errText(e.err);
            p.textContent = (e.name ? e.name + ': ' : '') + msg;
            box.appendChild(p);
        });
        box.hidden = !list.length;
    }

    function resetAll() {
        doc = null; undo = []; redo = []; active = 0; selection = null; cursorIdx = null; srcFiles = {};
        heatOff();
        var box = $('gtErrors'); if (box) { box.hidden = true; box.innerHTML = ''; }
        var nm = $('gtFileName'); if (nm) delete nm.dataset.touched;
    }

    // ── commit / undo ─────────────────────────────────────────────────────
    function commit(next, label, fresh) {
        if (!fresh && doc) { undo.push({ doc: doc, active: active, label: label }); if (undo.length > 60) undo.shift(); redo = []; }
        if (fresh) { undo = []; redo = []; }
        doc = next;
        if (active >= doc.tracks.length) active = Math.max(0, doc.tracks.length - 1);
        selection = null; cursorIdx = null;
        if (label) ae('feature_use', { f: label });
        render();
    }
    function doUndo() {
        var u = undo.pop(); if (!u) return;
        redo.push({ doc: doc, active: active, label: u.label });
        doc = u.doc; active = u.active; selection = null; cursorIdx = null;
        render();
        toast(t('undone'));
    }
    function doRedo() {
        var r = redo.pop(); if (!r) return;
        undo.push({ doc: doc, active: active, label: r.label });
        doc = r.doc; active = r.active; selection = null; cursorIdx = null;
        render();
    }

    // ── map ───────────────────────────────────────────────────────────────
    var skin = 'dark', satLayer = null, reliefLayer = null, baseLayer = null;
    function initMap() {
        if (map || typeof L === 'undefined') return;
        var Lf = L;
        map = Lf.map($('gtMap'), { worldCopyJump: true, zoomControl: false, attributionControl: true, preferCanvas: true })
            .setView([CFG.home[0], CFG.home[1]], CFG.home[2]);
        Lf.control.zoom({ position: RTL ? 'topleft' : 'topright' }).addTo(map);
        // the relief sits between the basemap (200) and the track canvas (400)
        map.createPane('gtRelief');
        map.getPane('gtRelief').style.zIndex = 250;
        map.getPane('gtRelief').style.pointerEvents = 'none';
        var tile = CFG.tile || {};
        if (window.__mapTiles) {
            baseLayer = window.__mapTiles.base(Lf, map, null, {
                slug: SLUG, styles: tile.styles, labels: tile.labels, control: false,
                opts: { maxZoom: 19 },
                onStyle: function (mode) {
                    if (satLayer && map.hasLayer(satLayer)) return;
                    skin = mode === 'light' ? 'light' : 'dark';
                    $('gtMap').setAttribute('data-basemap', skin);
                    if (layer) layer.setSkin(skin);
                },
                onExhausted: function () { var o = $('gtMapOffline'); if (o) { o.textContent = t('map_offline'); o.hidden = false; } }
            });
        } else if (window.__basemapFallback) {
            baseLayer = window.__basemapFallback(Lf, map, '');
        }
        layer = GpsMapLayer.create(Lf, map, {
            onHover: function (h) {
                if (heatOn) return;
                if (!h) { chart && chart.cursor(null); layer.setCursor(null); hideMapTip(); return; }
                var ti = trackIndexById(h.id);
                if (ti !== active) { showMapTip(h); layer.setCursor(h.id, h.idx); return; }
                cursorIdx = h.idx;
                layer.setCursor(h.id, h.idx);
                if (chart) chart.cursor(h.idx);
                showMapTip(h);
                renderCursorState();
            },
            onClick: function (h) {
                if (!h) return;
                var ti = trackIndexById(h.id);
                if (ti >= 0 && ti !== active) { active = ti; selection = null; renderFiles(); renderActive(); }
                cursorIdx = h.idx;
                if (chart) chart.cursor(h.idx);
                renderCursorState();
            },
            onWaypoint: function (i, w) {
                var html = '<b>' + escapeHtml(w.name || t('waypoint')) + '</b>' + (w.desc ? '<br>' + escapeHtml(w.desc) : '') + (w.ele != null && !isNaN(w.ele) ? '<br>' + U.ele(w.ele) : '');
                L.popup({ offset: [0, -18] }).setLatLng([w.lat, w.lon]).setContent(html).openOn(map);
            },
            onHeatClick: function (k) { if (k != null && heatLines[k]) openHeatEntry(heatLines[k]); }
        });
        layer.setSkin(skin);
        // Leaflet hears only window resizes; the frame changes size on its own
        // (the profile handle, fullscreen, the sidebar), so watch the box itself.
        if (typeof ResizeObserver !== 'undefined') {
            var ro = new ResizeObserver(function () { if (map) map.invalidateSize({ pan: false }); });
            ro.observe($('gtMap'));
        }
        applyBase(baseMode === 'sat' ? 'sat' : null, true);
        if (reliefOn) setRelief(true, true);
        applyGestures();
    }
    function trackIndexById(id) { return typeof id === 'number' ? id : -1; }

    /* Basemap: the brightness choice is shared by every map tool (map-tiles.js
     * keeps it); satellite is this tool's own, because a track on imagery is
     * what a hiker checks, while the other map tools have no use for it.
     * The shared basemap is ONE layer (map-tiles.js hands back its group), so
     * satellite takes it off the map and puts it back — no hunting for tile
     * layers by their URL. */
    function applyBase(mode, initial) {
        if (!map) return;
        if (mode === 'sat') {
            if (!satLayer) {
                var tile = CFG.tile || {};
                satLayer = GpsTerrain.chain(L, map, tile.sat || [], { attribution: tile.satAttr, maxNativeZoom: 19,
                    onExhausted: function () { toast(t('sat_failed'), 'warning'); applyBase('auto'); } });
            }
            if (baseLayer && map.hasLayer(baseLayer)) { try { map.removeLayer(baseLayer); } catch (_) {} }
            if (!map.hasLayer(satLayer)) satLayer.addTo(map);
            skin = 'dark';
            $('gtMap').setAttribute('data-basemap', 'dark');
            if (layer) layer.setSkin('dark');
            baseMode = 'sat';
        } else {
            if (satLayer && map.hasLayer(satLayer)) map.removeLayer(satLayer);
            if (baseLayer && !map.hasLayer(baseLayer)) { try { baseLayer.addTo(map); } catch (_) {} }
            baseMode = '';
            if (mode && window.__mapTiles) window.__mapTiles.setMode(mode);
            var m = window.__mapTiles ? window.__mapTiles.resolve() : 'dark';
            skin = m === 'light' ? 'light' : 'dark';
            $('gtMap').setAttribute('data-basemap', skin);
            if (layer) layer.setSkin(skin);
        }
        if (!initial) { save('base', baseMode); ae('feature_use', { f: 'base_' + (mode || 'auto') }); }
        syncLayerControls();
    }
    function setRelief(on, initial) {
        reliefOn = !!on;
        if (!map) return;
        if (on) {
            if (!reliefLayer) reliefLayer = GpsTerrain.hillshade(L, { urls: CFG.terrain || [], pane: 'gtRelief' });
            if (!map.hasLayer(reliefLayer)) reliefLayer.addTo(map);
        } else if (reliefLayer && map.hasLayer(reliefLayer)) map.removeLayer(reliefLayer);
        if (!initial) { save('relief', on ? '1' : '0'); ae('feature_use', { f: 'relief', on: on ? 1 : 0 }); }
        syncLayerControls();
    }
    function syncLayerControls() {
        var cur = baseMode === 'sat' ? 'sat' : (window.__mapTiles ? window.__mapTiles.mode() : 'auto');
        document.querySelectorAll('input[name=gtBase]').forEach(function (r) { r.checked = r.value === cur; });
        var rel = $('gtRelief'); if (rel) rel.checked = reliefOn;
        var km = $('gtKm'); if (km) km.checked = load('km', '1') === '1';
        var cm = $('gtClimbMarks'); if (cm) cm.checked = marksOn;
        var b = $('gtLayersBtn'); if (b) b.classList.toggle('on', baseMode === 'sat' || reliefOn);
    }

    var mapTip = null;
    function showMapTip(h) {
        var tr = doc && doc.tracks[h.id];
        if (!tr) return;
        var d = derived(tr), sr = d.sr, i = h.idx;
        var parts = [U.dist(sr.dist[i])];
        if (sr.ele && !isNaN(sr.ele[i])) parts.push(U.ele(sr.ele[i]));
        if (sr.time && !isNaN(sr.time[i]) && d.stats.start) parts.push(U.dur((sr.time[i] - d.stats.start) / 1000));
        if (!isNaN(d.speed[i])) parts.push(d.foot ? U.pace(d.speed[i]) : U.speed(d.speed[i]));
        if (sr.ch.hr && !isNaN(sr.ch.hr[i])) parts.push('♥ ' + Math.round(sr.ch.hr[i]));
        if (!mapTip) { mapTip = document.createElement('div'); mapTip.className = 'gt-maptip'; $('gtMapWrap').appendChild(mapTip); }
        var pt = map.latLngToContainerPoint([sr.lat[i], sr.lon[i]]);
        mapTip.textContent = parts.join(' · ');
        mapTip.hidden = false;
        var w = mapTip.offsetWidth, Wd = $('gtMap').clientWidth;
        mapTip.style.left = Math.max(6, Math.min(Wd - w - 6, pt.x - w / 2)) + 'px';
        mapTip.style.top = Math.max(6, pt.y - 44) + 'px';
    }
    function hideMapTip() { if (mapTip) mapTip.hidden = true; }

    function fitAll() {
        if (!map || !layer) return;
        setTimeout(function () { map.invalidateSize(); layer.fit(); }, 30);
    }

    function colorValues(tr, d) {
        var sr = d.sr;
        switch (colorBy) {
            case 'speed': return d.speed ? { v: d.speed } : null;
            case 'ele': return sr.ele ? { v: sr.ele } : null;
            case 'grade': return d.grade ? { v: d.grade, diverging: true, fixed: [-15, 15] } : null;
            case 'hr': case 'cad': case 'power': case 'temp': return sr.ch[colorBy] ? { v: sr.ch[colorBy] } : null;
        }
        return null;
    }
    function robustRange(arrs) {
        var s = [];
        arrs.forEach(function (a) { var step = Math.max(1, Math.floor(a.length / 5000)); for (var i = 0; i < a.length; i += step) if (a[i] === a[i]) s.push(a[i]); });
        if (!s.length) return null;
        s.sort(function (a, b) { return a - b; });
        return [s[Math.floor(s.length * 0.02)], s[Math.floor(s.length * 0.98)]];
    }

    function renderMap() {
        if (!layer || !doc) return;
        if (heatOn) return;
        var list = [];
        var vals = [];
        doc.tracks.forEach(function (tr, i) {
            var d = derived(tr);
            var cv = colorBy !== 'file' ? colorValues(tr, d) : null;
            if (cv) vals.push(cv);
            list.push({ id: i, sr: d.sr, color: tr.color, visible: tr.visible !== false, values: cv ? cv.v : null, cv: cv });
        });
        var range = null, diverging = false;
        if (vals.length) {
            if (vals[0].fixed) { range = vals[0].fixed; diverging = true; }
            else range = robustRange(vals.map(function (x) { return x.v; }));
        }
        list.forEach(function (x) { if (x.values && range) x.scale = { min: range[0], max: range[1], diverging: diverging }; else x.values = null; });
        layer.setTracks(list);
        layer.setWaypoints(doc.waypoints);
        layer.setKm(load('km', '1') === '1' ? { unit: U.unitLen() } : null);
        if (selection) layer.setSelection(active, selection.from, selection.to); else layer.setSelection(null);
        layer.setHighlight(null);
        renderMapMarks();
        renderLegend(range, diverging);
    }
    function renderMapMarks() {
        if (!layer) return;
        var tr = doc && doc.tracks[active];
        if (!marksOn || !tr || !derived(tr).sr.ele) { layer.setMarks(null); return; }
        layer.setMarks(climbsOf(tr).filter(function (c) { return c.cat; }).map(function (c) {
            return { id: active, from: c.from, to: c.to, label: catShort(c.cat), color: CAT_COLOR[c.cat] };
        }));
    }
    function catLabel(c) { return c === 'HC' ? t('cat_hc') : t('cat_n', { n: c }); }
    function catShort(c) { return c === 'HC' ? 'HC' : t('cat_n', { n: c }); }
    function renderLegend(range, diverging) {
        var el = $('gtLegend');
        if (!el) return;
        if (!range || colorBy === 'file') { el.hidden = true; return; }
        var fmt = legendFmt(colorBy);
        var stops = (diverging ? GpsMapLayer.DIV : GpsMapLayer.SEQ).map(function (s) { return 'rgb(' + s[1].join(',') + ') ' + Math.round(s[0] * 100) + '%'; }).join(',');
        el.innerHTML = '<span class="gt-leg-l"></span><span class="gt-leg-bar" style="background:linear-gradient(' + (RTL ? '270deg' : '90deg') + ',' + stops + ')"></span><span class="gt-leg-r"></span>';
        el.querySelector('.gt-leg-l').textContent = fmt(range[0]);
        el.querySelector('.gt-leg-r').textContent = fmt(range[1]);
        el.hidden = false;
    }
    function legendFmt(k) {
        var tr = doc.tracks[active], foot = tr ? derived(tr).foot : false;
        switch (k) {
            case 'speed': return function (v) { return foot ? U.pace(v) : U.speed(v); };
            case 'ele': return U.ele;
            case 'grade': return function (v) { return NF0.format(v) + '%'; };
            case 'hr': return function (v) { return NF0.format(v) + ' ' + t('u_bpm'); };
            case 'cad': return function (v) { return NF0.format(v) + ' ' + t('u_rpm'); };
            case 'power': return function (v) { return NF0.format(v) + ' ' + t('u_w'); };
            case 'temp': return function (v) { return NF1.format(v) + ' °C'; };
        }
        return String;
    }

    // ── chart ─────────────────────────────────────────────────────────────
    function css(v, fb) { try { var s = getComputedStyle($('gtApp')).getPropertyValue(v).trim(); return s || fb; } catch (_) { return fb; } }
    function isLight() { return document.documentElement.getAttribute('data-theme') === 'light'; }
    function initChart() {
        if (chart) return;
        chart = GpsChart.create($('gtChart'), {
            colors: function () {
                var light = isLight();
                return { text: css('--lg-text', light ? '#1b1b1f' : '#e7e9ee'), muted: css('--lg-text-3', light ? '#6b7280' : '#8b93a3'), grid: light ? 'rgba(0,0,0,.08)' : 'rgba(255,255,255,.08)', sel: 'rgba(255,45,122,.16)', hl: 'rgba(255,170,40,.16)', cursor: '#ff2d7a', bgSolid: light ? '#fff' : '#111', pill: light ? 'rgba(255,255,255,.9)' : 'rgba(17,19,24,.78)', pillText: light ? '#1b1b1f' : '#fff', lineOnShade: light ? 'rgba(20,24,32,.85)' : 'rgba(240,242,248,.92)' };
            },
            onHover: function (i) {
                if (i == null) { layer && layer.setCursor(null); hideMapTip(); return; }
                cursorIdx = i;
                if (layer) layer.setCursor(active, i);
                renderCursorState();
            },
            onSelect: function (a, b) {
                if (a == null) { selection = null; if (layer) layer.setSelection(null); renderSel(); renderEditState(); return; }
                selection = { from: a, to: b };
                if (layer) layer.setSelection(active, a, b);
                renderSel();
                renderEditState();
                ae('feature_use', { f: 'select' });
            },
            onPick: function (i) { cursorIdx = i; renderCursorState(); }
        });
    }

    var PANEL_COLORS = { ele: '#2fb37a', speed: '#3e7bfa', pace: '#3e7bfa', hr: '#e5484d', cad: '#8e4ec6', power: '#f76b15', temp: '#12a594', grade: '#b08800', depth: '#0090ff' };
    function panelsFor(tr) {
        var d = derived(tr), sr = d.sr, out = [];
        var chs = [];
        if (sr.ele) chs.push({ key: 'ele', label: t('p_ele'), v: scaleArr(sr.ele, U.eleK()), fill: true, w: 2.4, minH: 48, fmt: function (v) { return NF0.format(v) + ' ' + (units === 'imperial' ? t('u_ft') : t('u_m')); }, minSpan: 20 });
        if (sr.time) {
            if (d.foot) chs.push({ key: 'pace', label: t('p_pace'), v: paceArr(d.speed), invert: true, fmt: function (v) { return fmtPaceSec(v); }, tick: fmtPaceSec, robust: true, minSpan: 30 });
            else chs.push({ key: 'speed', label: t('p_speed'), v: scaleArr(d.speed, U.spdK()), fmt: function (v) { return NF1.format(v) + ' ' + (units === 'imperial' ? t('u_mph') : t('u_kmh')); }, robust: true, zero: true });
        }
        if (sr.ch.hr) chs.push({ key: 'hr', label: t('p_hr'), v: sr.ch.hr, fmt: function (v) { return NF0.format(v) + ' ' + t('u_bpm'); }, minSpan: 10 });
        if (sr.ch.cad) chs.push({ key: 'cad', label: t('p_cad'), v: sr.ch.cad, w: 0.8, fmt: function (v) { return NF0.format(v) + ' ' + t('u_rpm'); }, zero: true });
        if (sr.ch.power) chs.push({ key: 'power', label: t('p_power'), v: sr.ch.power, fmt: function (v) { return NF0.format(v) + ' ' + t('u_w'); }, zero: true, robust: true });
        if (sr.ch.temp) chs.push({ key: 'temp', label: t('p_temp'), v: sr.ch.temp, w: 0.7, fmt: function (v) { return NF1.format(v) + ' °C'; }, minSpan: 4 });
        if (sr.ch.depth) chs.push({ key: 'depth', label: t('p_depth'), v: sr.ch.depth, w: 1.2, invert: true, fmt: function (v) { return NF1.format(v) + ' ' + t('u_m'); }, zero: true });
        if (d.grade) chs.push({ key: 'grade', label: t('p_grade'), v: d.grade, w: 0.8, fmt: function (v) { return NF1.format(v) + '%'; }, minSpan: 4 });
        chs.forEach(function (p) { p.color = PANEL_COLORS[p.key]; if (!hiddenPanels[p.key]) out.push(p); });
        // the elevation fill takes the colour of the local grade
        out.forEach(function (p) { if (p.key === 'ele' && shadeOn && d.grade) p.shade = shadeOf(tr); });
        return { shown: out, all: chs };
    }
    function scaleArr(a, k) { if (k === 1) return a; var o = new Float32Array(a.length); for (var i = 0; i < a.length; i++) o[i] = a[i] * k; return o; }
    function paceArr(sp) {
        var Lu = U.unitLen(), o = new Float32Array(sp.length);
        for (var i = 0; i < sp.length; i++) { var v = sp[i]; o[i] = v > 0.5 ? Lu / v : NaN; if (o[i] > 1800) o[i] = NaN; }
        return o;
    }
    function fmtPaceSec(s) { if (!(s > 0)) return '—'; var m = Math.floor(s / 60), ss = Math.round(s % 60); if (ss === 60) { m++; ss = 0; } return m + ':' + (ss < 10 ? '0' : '') + ss; }

    function renderChart() {
        initChart();
        var tr = doc && doc.tracks[active];
        var wrap = $('gtChartP');
        if (!tr) { wrap.hidden = true; return; }
        var d = derived(tr), sr = d.sr;
        var P = panelsFor(tr);
        renderPanelChips(P.all);
        var timeOk = sr.time && d.stats.elapsed > 0 && timeMonotone(sr);
        var xk = xKind === 'time' && timeOk ? 'time' : 'dist';
        $('gtXDist').classList.toggle('on', xk === 'dist');
        $('gtXTime').classList.toggle('on', xk === 'time');
        $('gtXTime').disabled = !timeOk;
        var sh = $('gtShade');
        sh.hidden = !d.grade;
        sh.classList.toggle('on', shadeOn);
        sh.setAttribute('aria-pressed', shadeOn ? 'true' : 'false');
        if (!P.shown.length || sr.n < 2) { wrap.hidden = !P.all.length || sr.n < 2; chart.set(null); syncResize(); return; }
        wrap.hidden = false;
        var x;
        if (xk === 'time') {
            x = new Float64Array(sr.n);
            var t0 = d.stats.start;
            for (var i = 0; i < sr.n; i++) x[i] = isNaN(sr.time[i]) ? (i ? x[i - 1] : 0) : (sr.time[i] - t0) / 1000;
        } else {
            x = new Float64Array(sr.n);
            var k = 1 / U.unitLen();
            for (var j = 0; j < sr.n; j++) x[j] = sr.dist[j] * k;
        }
        var marks = sr.ele && !hiddenPanels.ele ? climbsOf(tr).filter(function (c) { return c.cat; }).map(function (c) { return { from: c.from, to: c.to, label: catShort(c.cat), color: CAT_COLOR[c.cat] }; }) : null;
        chart.set({
            x: x, xKind: xk, panels: P.shown, breaks: sr.segStart.slice(1), marks: marks,
            xUnit: xk === 'dist' ? (units === 'imperial' ? t('u_mi') : t('u_km')) : '',
            gradeLabel: t('p_grade'),
            fmtX: xk === 'time' ? function (v) { return U.dur(v); } : function (v, step) { return (step < 1 ? NF1.format(v) : NF0.format(v)); },
            xStep: xk === 'time' ? timeStep : null,
            fmtTip: function (i) {
                var s = U.dist(sr.dist[i]);
                if (sr.time && d.stats.start && !isNaN(sr.time[i])) s += ' · ' + U.dur((sr.time[i] - d.stats.start) / 1000);
                return s;
            },
            aria: t('chart_aria', { d: U.dist(d.stats.distance) })
        });
        syncResize();
        if (selection) chart.select(selection.from, selection.to);
        if (cursorIdx != null) chart.cursor(cursorIdx);
    }
    function timeStep(span, count) {
        var raw = span / count;
        var steps = [30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400];
        for (var i = 0; i < steps.length; i++) if (steps[i] >= raw) return steps[i];
        return 86400 * Math.ceil(raw / 86400);
    }
    function timeMonotone(sr) {
        var last = -Infinity, bad = 0;
        for (var i = 0; i < sr.n; i++) { var v = sr.time[i]; if (isNaN(v)) continue; if (v < last) bad++; last = v; }
        return bad < sr.n * 0.01;
    }
    function renderPanelChips(all) {
        var box = $('gtPanels');
        box.innerHTML = '';
        all.forEach(function (p) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'gt-chip' + (hiddenPanels[p.key] ? '' : ' on');
            b.setAttribute('aria-pressed', hiddenPanels[p.key] ? 'false' : 'true');
            b.innerHTML = '<i style="background:' + p.color + '"></i>';
            b.appendChild(document.createTextNode(p.label));
            b.addEventListener('click', function () {
                if (hiddenPanels[p.key]) delete hiddenPanels[p.key]; else hiddenPanels[p.key] = 1;
                save('hidden', JSON.stringify(hiddenPanels));
                renderChart();
                ae('feature_use', { f: 'panel_' + p.key, on: hiddenPanels[p.key] ? 0 : 1 });
            });
            box.appendChild(b);
        });
    }

    /* The profile's height: what the person dragged it to, never less than the
     * bands on show need to stay readable. */
    function syncResize() {
        var fr = $('gtWork');
        var need = chart ? chart.minHeight() : 0;
        if (profH) fr.style.setProperty('--gt-prof-h', Math.max(profH, need) + 'px');
        else if (need > 170) fr.style.setProperty('--gt-prof-h', need + 'px');
        else fr.style.removeProperty('--gt-prof-h');
    }
    function bindResize() {
        var h = $('gtResize'), fr = $('gtWork');
        if (!h) return;
        var start = null;
        function cur() { return $('gtChart').getBoundingClientRect().height; }
        function maxH() {
            var fr = $('gtWork').getBoundingClientRect(), bar = document.querySelector('.gt-bar');
            return Math.max(160, fr.height - (bar ? bar.offsetHeight : 48) - 260);
        }
        function set(v, persist) {
            var need = chart ? chart.minHeight() : 110;
            v = Math.round(Math.max(Math.max(110, need), Math.min(maxH(), v)));
            profH = v;
            fr.style.setProperty('--gt-prof-h', v + 'px');
            if (persist) save('profh', String(v));
        }
        h.addEventListener('pointerdown', function (e) {
            if (e.button !== 0 && e.pointerType === 'mouse') return;
            start = { y: e.clientY, h: cur() };
            h.classList.add('drag');
            try { h.setPointerCapture(e.pointerId); } catch (_) {}
            e.preventDefault();
        });
        h.addEventListener('pointermove', function (e) { if (start) set(start.h + (start.y - e.clientY), false); });
        function end() { if (!start) return; start = null; h.classList.remove('drag'); save('profh', String(profH)); ae('feature_use', { f: 'profile_resize', h: profH }); }
        h.addEventListener('pointerup', end);
        h.addEventListener('pointercancel', end);
        h.addEventListener('dblclick', function () { profH = 0; save('profh', '0'); syncResize(); });
        h.addEventListener('keydown', function (e) {
            if (e.key === 'ArrowUp') { set(cur() + 16, true); e.preventDefault(); }
            else if (e.key === 'ArrowDown') { set(cur() - 16, true); e.preventDefault(); }
            else if (e.key === 'Home') { profH = 0; save('profh', '0'); syncResize(); e.preventDefault(); }
        });
    }

    // ── stats ─────────────────────────────────────────────────────────────
    /* A start time is shown in the zone of the PLACE, not of the browser: a ride
     * in the Alps opened in Adelaide started at 08:40, not at 16:10. tz-lookup
     * (73 KB) is fetched when a person first reaches for a file; until it is
     * here the time is not shown at all, and if it never comes the time is
     * given in UTC and says so — never silently in the viewer's zone. */
    var tzState = 'idle';
    function loadTz() {
        if (tzState !== 'idle' || !CFG.tzUrl) return;
        if (typeof window.tzlookup === 'function') { tzState = 'ready'; return; }
        tzState = 'loading';
        var s = document.createElement('script');
        s.src = CFG.tzUrl;
        s.onload = function () { tzState = typeof window.tzlookup === 'function' ? 'ready' : 'failed'; if (doc) renderStats(); };
        s.onerror = function () { tzState = 'failed'; if (doc) renderStats(); };
        document.head.appendChild(s);
    }
    function tzOf(lat, lon) {
        loadTz();
        if (tzState === 'ready') { try { return window.tzlookup(lat, lon); } catch (_) { return 'UTC'; } }
        return tzState === 'failed' || !CFG.tzUrl ? 'UTC' : null;
    }
    function fmtDate(ms, lat, lon, timeOnly) {
        if (ms == null || isNaN(ms)) return '—';
        var tz = tzOf(lat, lon);
        if (!tz) return '…';
        try {
            var o = timeOnly ? { hour: '2-digit', minute: '2-digit', timeZone: tz } : { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz };
            return new Intl.DateTimeFormat(LOC, o).format(new Date(ms)) + (tz === 'UTC' ? ' UTC' : '');
        } catch (_) { return new Date(ms).toLocaleString(LOC); }
    }

    function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

    // «1 116 м» → number in bold, unit smaller
    function bigNum(v) {
        var m = /^(↑\s*)?(.*?)(\s[^\d\s:]+)?$/.exec(String(v)) || [];
        return (m[1] ? '<i>↑</i>' : '') + escapeHtml(m[2] || v) + (m[3] ? '<span>' + escapeHtml(m[3]) + '</span>' : '');
    }

    function renderStats() {
        if (!doc) return;
        var tr = doc.tracks[active];
        var hero = $('gtHero'), kvBox = $('gtStats'), meta = $('gtTrackMeta');
        if (!tr) {
            hero.innerHTML = '';
            meta.textContent = doc.waypoints.length ? t('only_waypoints', { n: doc.waypoints.length }) : t('err_empty');
            kvBox.innerHTML = '';
            $('gtEleNote').hidden = true; $('gtDevice').hidden = true;
            return;
        }
        var d = derived(tr), st = d.stats, sr = d.sr;
        // meta: sport · start · device
        var bits = [];
        if (tr.sport) bits.push('<b>' + escapeHtml(sportLabel(tr.sport)) + '</b>');
        if (st.start) bits.push('<span>' + escapeHtml(fmtDate(st.start, sr.lat[0], sr.lon[0])) + '</span>');
        var devName = (doc.device || '').trim();
        if (devName && doc.tracks.length === 1) bits.push('<span>' + escapeHtml(devName) + '</span>');
        meta.innerHTML = bits.join('<span aria-hidden="true">·</span>');
        // the four numbers people come for
        var cells = [];
        var cell = function (l, v, sub, cls) { cells.push('<div class="gt-m' + (cls ? ' ' + cls : '') + '"><small>' + escapeHtml(l) + '</small><b>' + bigNum(v) + '</b>' + (sub ? '<em>' + escapeHtml(sub) + '</em>' : '<em>&nbsp;</em>') + '</div>'); };
        cell(t('s_dist'), U.dist(st.distance), st.segments > 1 ? t('s_segments', { n: st.segments }) : '');
        if (st.moving != null) cell(t('hs_moving'), U.dur(st.moving), st.elapsed && st.elapsed - st.moving > 60 ? t('s_elapsed_sub', { t: U.dur(st.elapsed) }) : '');
        var gn = gainOf(tr, d);
        if (gn.gain != null) cell(t('hs_gain'), '↑ ' + U.ele(gn.gain), gn.loss != null ? '↓ ' + U.ele(gn.loss) : '', 'up');
        if (st.moving) {
            if (d.foot) cell(t('hs_pace'), U.pace(st.movingSpeed), U.speed(st.movingSpeed));
            else cell(t('hs_speed'), U.speed(st.movingSpeed), st.maxSpeed ? t('s_max', { v: U.speed(st.maxSpeed) }) : '');
        }
        if (st.moving == null && st.distance > 50 && cells.length < 4) {
            var ef = estOf(tr, 'foot'), eb = estOf(tr, 'bike');
            if (ef) cell(t('s_est_foot'), '≈ ' + U.durShort(ef.sec), t('s_est_at', { v: U.speed(spd('foot')) }));
            if (eb && cells.length < 4) cell(t('s_est_bike'), '≈ ' + U.durShort(eb.sec), t('s_est_at', { v: U.speed(spd('bike')) }));
        }
        if (cells.length < 4 && st.climb && st.climb.max != null && gn.gain == null) cell(t('s_ele_range'), U.ele(st.climb.max), U.ele(st.climb.min) + ' – ' + U.ele(st.climb.max));
        if (cells.length < 4) cell(t('s_points'), NF0.format(sr.n), '');
        hero.innerHTML = cells.slice(0, 4).join('');
        // details
        var kv = [];
        var row = function (l, v, sub, wide) { kv.push('<div' + (wide ? ' class="wide"' : '') + '><dt>' + escapeHtml(l) + '</dt><dd><bdi>' + escapeHtml(v) + '</bdi>' + (sub ? ' <small><bdi>' + escapeHtml(sub) + '</bdi></small>' : '') + '</dd></div>'); };
        if (st.elapsed) row(t('s_elapsed'), U.dur(st.elapsed));
        if (st.moving && st.elapsed) row(t('s_avg_total'), d.foot ? U.pace(st.distance / st.elapsed) : U.speed(st.distance / st.elapsed));
        if (st.maxSpeed) row(t('s_max_speed'), U.speed(st.maxSpeed));
        if (st.climb && st.climb.min != null) row(t('s_ele_range'), U.ele(st.climb.min) + ' – ' + U.ele(st.climb.max));
        var mg = d.sr.ele ? steepOf(tr) : null;
        var PCT = String.fromCharCode(0x202F) + '%'; // narrow no-break space: «16,1 %» never breaks before the sign
        if (mg && (mg.up >= 0.5 || mg.down >= 0.5)) row(t('s_max_grade'), '↑' + NF1.format(mg.up) + PCT + '  ↓' + NF1.format(mg.down) + PCT);
        if (st.hr) row(t('s_hr'), NF0.format(st.hr.avg) + ' ' + t('u_bpm'), t('s_max', { v: NF0.format(st.hr.max) }));
        if (st.cad) row(t('s_cad'), NF0.format(st.cad.avg) + ' ' + t('u_rpm'));
        if (st.power) {
            var pc = powerOf(tr);
            row(t('s_power'), NF0.format(st.power.avg) + ' ' + t('u_w'), pc && pc.np ? t('s_np', { v: NF0.format(pc.np) }) : t('s_max', { v: NF0.format(st.power.max) }));
        }
        if (st.temp) row(t('s_temp'), NF1.format(st.temp.avg) + ' °C', NF0.format(st.temp.min) + '…' + NF0.format(st.temp.max));
        if (st.depth) row(t('s_depth'), NF1.format(st.depth.max) + ' ' + t('u_m'));
        if (tr.device && tr.device.calories && !tr.edited) row(t('s_kcal'), NF0.format(tr.device.calories) + ' ' + t('u_kcal'));
        if (st.start) row(t('s_start'), fmtDate(st.start, sr.lat[0], sr.lon[0]), '', true);
        if (st.end && st.elapsed > 0) row(t('s_finish'), fmtDate(st.end, sr.lat[sr.n - 1], sr.lon[sr.n - 1]), '', true);
        row(t('s_points'), NF0.format(sr.n), st.segments > 1 ? t('s_segments', { n: st.segments }) : '');
        if (doc.format) row(t('s_format'), String(doc.format).toUpperCase());
        kvBox.innerHTML = kv.join('');
        // device totals, when the file carries them
        var dev = $('gtDevice');
        if (tr.device && !tr.edited && (tr.device.distance || tr.device.ascent != null)) {
            var db = [];
            if (tr.device.distance) db.push(U.dist(tr.device.distance));
            if (tr.device.timer) db.push(U.dur(tr.device.timer));
            if (tr.device.ascent != null) db.push('↑ ' + U.ele(tr.device.ascent));
            dev.textContent = (devName ? t('device_totals', { d: devName }) : t('device_totals_anon')) + ' ' + db.join(' · ');
            dev.hidden = false;
        } else dev.hidden = true;
        renderEleNote(tr, d);
    }

    function renderEleNote(tr, d) {
        var box = $('gtEleNote'), st = d.stats;
        var html = '', cls = '';
        var btn = function (id, label) { return ' <button type="button" class="tc-btn ghost sm" id="' + id + '">' + escapeHtml(label) + '</button>'; };
        if (!d.sr.ele || st.climb.min == null) { html = escapeHtml(t('ele_none')) + btn('gtDemGo', t('ele_dem_btn')); cls = 'warn'; }
        else if (gainOf(tr, d).src === 'device') html = escapeHtml(t('ele_device'));
        else if (tr.eleSource === 'dem') { html = escapeHtml(t('ele_dem_done')) + (tr._eleBackup ? btn('gtDemUndo', t('ele_dem_undo')) : ''); cls = 'good'; }
        else if (st.climb.source === 'baro') html = escapeHtml(t('ele_baro'));
        else { html = escapeHtml(t('ele_gps')) + btn('gtDemGo', t('ele_dem_btn')); cls = 'warn'; }
        box.className = 'tc-msg ' + cls;
        box.innerHTML = html;
        box.hidden = false;
        var go = $('gtDemGo'); if (go) go.addEventListener('click', runDem);
        var un = $('gtDemUndo'); if (un) un.addEventListener('click', function () { doUndo(); });
    }

    function sportLabel(s) {
        var k = 'sport_' + String(s).toLowerCase().replace(/[^a-z_]/g, '');
        return S[k] ? S[k] : String(s).replace(/_/g, ' ');
    }

    // ── selection bar (over the map) ──────────────────────────────────────
    function renderSel() {
        var bar = $('gtSelBar');
        if (!selection || !doc || !doc.tracks[active]) { bar.hidden = true; return; }
        var d = derived(doc.tracks[active]);
        var st = G.stats(d.sr, { from: selection.from, to: selection.to, speed: d.speed });
        var bits = [U.dist(st.distance)];
        if (st.elapsed != null) bits.push(U.dur(st.moving != null ? st.moving : st.elapsed));
        if (st.climb && st.climb.gain != null) bits.push('↑ ' + U.ele(st.climb.gain) + ' ↓ ' + U.ele(st.climb.loss));
        if (st.moving) bits.push(d.foot ? U.pace(st.movingSpeed) : U.speed(st.movingSpeed));
        if (st.hr) bits.push('♥ ' + NF0.format(st.hr.avg));
        if (st.power) bits.push(NF0.format(st.power.avg) + ' ' + t('u_w'));
        $('gtSelText').textContent = t('sel_label') + ' ' + bits.join(' · ');
        bar.hidden = false;
    }
    /* Selecting a stretch from a list (a climb, a split, a best effort) does
     * what a drag on the profile does, and shows it on the map. */
    function selectRange(from, to, fit) {
        if (!doc.tracks[active] || to <= from) return;
        selection = { from: from, to: to };
        if (chart) chart.select(from, to);
        if (layer) { layer.setHighlight(null); layer.setSelection(active, from, to); if (fit) layer.fitRange(active, from, to); }
        renderSel();
        renderEditState();
    }
    function hoverRange(from, to) {
        if (from == null) { if (chart) chart.highlight(null); if (layer) layer.setHighlight(null); return; }
        if (chart) chart.highlight(from, to);
        if (layer) layer.setHighlight(active, from, to);
    }

    // ── the parts tab: splits, laps, climbs, best efforts, power, points ───
    function partsAvailable(tr) {
        var out = [];
        if (!tr) { if (doc.waypoints.length) out.push('wpts'); return out; }
        var d = derived(tr);
        if (d.sr.n > 1 && d.stats.distance > U.unitLen() * 0.5) out.push('splits');
        if (d.sr.ele) out.push('climbs');
        if (tr.laps && tr.laps.length > 1) out.push('laps');
        if (d.sr.time && d.stats.distance >= (d.foot ? 400 : 5000)) out.push('best');
        if (d.sr.ch.power && d.sr.time) out.push('power');
        if (doc.waypoints.length) out.push('wpts');
        return out;
    }
    function renderParts() {
        if (!doc) return;
        var tr = doc.tracks[active];
        var list = partsAvailable(tr);
        var tabBtn = $('gtTabParts');
        tabBtn.hidden = !list.length;
        if (!list.length) { if (tab === 'parts') setTab('stats'); return; }
        var cur = list.indexOf(part) >= 0 ? part : list[0];
        var seg = $('gtPartSeg');
        seg.innerHTML = '';
        list.forEach(function (k) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = k === cur ? 'on' : '';
            b.dataset.part = k;
            b.setAttribute('aria-pressed', k === cur ? 'true' : 'false');
            var n = k === 'wpts' ? doc.waypoints.length : k === 'laps' ? tr.laps.length : k === 'climbs' ? climbsOf(tr).length : 0;
            b.textContent = (k === 'splits' ? (units === 'imperial' ? t('part_splits_mi') : t('part_splits')) : t('part_' + k)) + (n ? ' · ' + n : '');
            b.addEventListener('click', function () { part = k; save('part', k); renderParts(); ae('feature_use', { f: 'part_' + k }); });
            seg.appendChild(b);
        });
        var body = $('gtPartBody');
        if (tab !== 'parts') { body.innerHTML = ''; return; } // built when looked at
        if (cur === 'splits') body.innerHTML = splitsTable(tr);
        else if (cur === 'laps') body.innerHTML = lapsTable(tr);
        else if (cur === 'climbs') body.innerHTML = climbsList(tr);
        else if (cur === 'best') body.innerHTML = bestTable(tr);
        else if (cur === 'power') body.innerHTML = powerTable(tr);
        else body.innerHTML = wptTable();
        wireRanges(body);
        if (cur === 'wpts') {
            body.querySelectorAll('[data-w]').forEach(function (r) {
                r.addEventListener('click', function (e) {
                    if (e.target.closest('[data-wdel]')) return;
                    var w = doc && doc.waypoints[+r.getAttribute('data-w')]; if (w && map) map.setView([w.lat, w.lon], Math.max(map.getZoom(), 15));
                });
            });
            body.querySelectorAll('[data-wdel]').forEach(function (b) {
                b.addEventListener('click', function () { if (!doc) return; commit(E.removeWaypoint(doc, +b.getAttribute('data-wdel')), 'wpt_remove'); });
            });
        }
    }
    function wireRanges(body) {
        body.querySelectorAll('[data-r]').forEach(function (el) {
            var r = el.getAttribute('data-r').split('-').map(Number);
            el.addEventListener('click', function () { selectRange(r[0], r[1], true); ae('feature_use', { f: 'part_pick' }); });
            el.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectRange(r[0], r[1], true); } });
            el.addEventListener('pointerenter', function () { hoverRange(r[0], r[1]); });
            el.addEventListener('pointerleave', function () { hoverRange(null); });
            el.addEventListener('focus', function () { hoverRange(r[0], r[1]); });
            el.addEventListener('blur', function () { hoverRange(null); });
        });
    }
    function tbl(head, rows) { return '<div class="gt-tblwrap"><table class="gt-tbl"><thead><tr>' + head.map(function (h) { return '<th>' + escapeHtml(h) + '</th>'; }).join('') + '</tr></thead><tbody>' + rows.join('') + '</tbody></table></div>'; }
    function splitsTable(tr) {
        var d = derived(tr), sp = G.splits(d.sr, U.unitLen());
        var hasHr = !!d.sr.ch.hr, hasPw = !!d.sr.ch.power;
        // headers are units, not words: six columns share a 290 px rail
        var head = [units === 'imperial' ? t('u_mi') : t('u_km'), t('col_time'), d.foot ? (units === 'imperial' ? t('u_per_mi') : t('u_per_km')) : (units === 'imperial' ? t('u_mph') : t('u_kmh')), '↑/↓ ' + (units === 'imperial' ? t('u_ft') : t('u_m'))];
        if (hasHr) head.push('♥'); if (hasPw) head.push(t('u_w'));
        var best = Infinity;
        sp.forEach(function (s) { var tt = s.moving != null ? s.moving : s.elapsed; if (tt && s.distance > U.unitLen() * 0.9) best = Math.min(best, tt / s.distance); });
        var acc = 0;
        var rows = sp.map(function (s, i) {
            acc += s.distance;
            var tt = s.moving != null ? s.moving : s.elapsed;
            var v = tt ? s.distance / tt : null;
            var isBest = tt && s.distance > U.unitLen() * 0.9 && Math.abs(tt / s.distance - best) < 1e-9;
            var label = s.distance < U.unitLen() * 0.99 ? NF2.format(acc / U.unitLen()) : String(i + 1);
            var sp2 = v ? (d.foot ? U.pace(v).replace(/\s.*$/, '') : NF1.format(v * U.spdK())) : '—';
            return '<tr tabindex="0" data-r="' + s.from + '-' + s.to + '"' + (isBest ? ' class="best"' : '') + '><td>' + label + '</td><td>' + escapeHtml(tt ? U.dur(tt) : '—') + '</td><td>' + escapeHtml(sp2) + '</td><td class="ud">' + escapeHtml(s.gain != null ? '+' + NF0.format(s.gain * U.eleK()) + '/−' + NF0.format(s.loss * U.eleK()) : '—') + '</td>' + (hasHr ? '<td>' + (s.hr ? NF0.format(s.hr) : '—') + '</td>' : '') + (hasPw ? '<td>' + (s.power ? NF0.format(s.power) : '—') + '</td>' : '') + '</tr>';
        });
        return tbl(head, rows);
    }
    function lapsTable(tr) {
        var head = ['#', t('s_dist'), t('col_time'), t('p_speed'), '↑', '♥'];
        var rows = tr.laps.map(function (l, i) {
            var tt = l.timer || l.elapsed;
            return '<tr><td>' + (i + 1) + '</td><td>' + escapeHtml(l.distance != null ? U.dist(l.distance) : '—') + '</td><td>' + escapeHtml(tt ? U.dur(tt) : '—') + '</td><td>' + escapeHtml(tt && l.distance ? U.speed(l.distance / tt) : '—') + '</td><td>' + escapeHtml(l.ascent != null ? U.ele(l.ascent) : '—') + '</td><td>' + (l.avgHr ? NF0.format(l.avgHr) : '—') + '</td></tr>';
        });
        return tbl(head, rows);
    }
    function climbsList(tr) {
        var cl = climbsOf(tr);
        if (!cl.length) return '<p class="gt-empty">' + escapeHtml(t('climbs_none')) + '</p>';
        var total = cl.reduce(function (a, c) { return a + c.gain; }, 0);
        var html = '<p class="gt-np">' + escapeHtml(t('climbs_sum', { n: cl.length, g: U.ele(total) })) + '</p><ul class="gt-climbs">';
        cl.forEach(function (c) {
            var line2 = [U.dist(c.dist), t('climb_avg', { g: NF1.format(c.grade) }), t('climb_max', { g: NF0.format(c.maxGrade) })];
            var line3 = [];
            if (c.sec) line3.push(U.dur(c.sec));
            if (c.vam) line3.push(t('climb_vam', { v: NF0.format(c.vam) }));
            line3.push(U.ele(c.ele0) + ' → ' + U.ele(c.ele1));
            html += '<li><button type="button" class="gt-climb" data-r="' + c.from + '-' + c.to + '"><span class="gt-cat" data-c="' + (c.cat || '') + '">' + escapeHtml(c.cat ? (c.cat === 'HC' ? 'HC' : c.cat) : '—') + '</span><b>↑ ' + escapeHtml(U.ele(c.gain)) + ' · ' + escapeHtml(line2.join(' · ')) + '</b><small>' + escapeHtml((c.cat ? catLabel(c.cat) + ' · ' : '') + line3.join(' · ')) + '</small></button></li>';
        });
        return html + '</ul><p class="tc-hint" style="margin-top:8px">' + escapeHtml(t('climbs_hint')) + '</p>';
    }
    function effortLabel(dm) {
        var names = { 804.672: t('be_half_mile'), 1609.344: t('be_mile'), 3218.688: t('be_2mi'), 16093.44: t('be_10mi'), 21097.5: t('be_half'), 42195: t('be_marathon'), 160934.4: t('be_100mi') };
        if (names[dm]) return names[dm];
        return dm < 1000 ? NF0.format(dm) + ' ' + t('u_m') : NF0.format(dm / 1000) + ' ' + t('u_km');
    }
    function bestTable(tr) {
        var d = derived(tr), be = bestOf(tr);
        if (!be.length) return '<p class="gt-empty">' + escapeHtml(t('best_none')) + '</p>';
        var rows = be.map(function (b) {
            return '<tr tabindex="0" data-r="' + b.from + '-' + b.to + '"><td>' + escapeHtml(effortLabel(b.d)) + '</td><td>' + escapeHtml(U.dur(b.sec)) + '</td><td>' + escapeHtml(d.foot ? U.pace(b.speed) : U.speed(b.speed)) + '</td></tr>';
        });
        return tbl([t('be_dist'), t('col_time'), d.foot ? t('p_pace') : t('p_speed')], rows) + '<p class="tc-hint" style="margin-top:8px">' + escapeHtml(t('best_hint')) + '</p>';
    }
    function durLabel(s) { return s < 60 ? t('pw_s', { n: s }) : s < 3600 ? t('pw_min', { n: s / 60 }) : t('pw_h', { n: s / 3600 }); }
    function powerTable(tr) {
        var pc = powerOf(tr);
        if (!pc || !pc.best.length) return '<p class="gt-empty">' + escapeHtml(t('best_none')) + '</p>';
        var rows = pc.best.map(function (b) { return '<tr tabindex="0" data-r="' + b.from + '-' + b.to + '"><td>' + escapeHtml(durLabel(b.s)) + '</td><td>' + NF0.format(b.w) + ' ' + escapeHtml(t('u_w')) + '</td></tr>'; });
        return (pc.np ? '<p class="gt-np">' + escapeHtml(t('pw_np', { v: NF0.format(pc.np) })) + '</p>' : '') + tbl([t('pw_dur'), t('pw_avg')], rows);
    }
    function wptTable() {
        var rows = doc.waypoints.slice(0, 2000).map(function (w, i) {
            return '<tr data-w="' + i + '" style="cursor:pointer"><td class="nm">' + escapeHtml(w.name || t('waypoint')) + (w.desc ? '<div class="tc-hint">' + escapeHtml(w.desc.slice(0, 140)) + '</div>' : '') + '</td><td>' + escapeHtml(w.ele != null && !isNaN(w.ele) ? U.ele(w.ele) : '—') + '</td><td><button type="button" class="gt-ib" data-wdel="' + i + '" title="' + escapeHtml(t('wpt_remove')) + '" aria-label="' + escapeHtml(t('wpt_remove')) + '" style="height:28px;min-width:28px">✕</button></td></tr>';
        });
        return tbl([t('col_name'), t('p_ele'), ''], rows);
    }

    // ── notes (file info & warnings) ──────────────────────────────────────
    function renderNotes() {
        if (!doc) return;
        var box = $('gtNotes');
        var items = [];
        var ws = doc.warnings || [];
        ws.forEach(function (w) {
            var m = /^([a-z-]+):?(\d+)?/.exec(w) || [];
            var k = m[1], n = m[2];
            if (k === 'fit-truncated') items.push(escapeHtml(t('w_fit_truncated')));
            else if (k === 'fit-crc') items.push(escapeHtml(t('w_fit_crc')));
            else if (k === 'chained') items.push(escapeHtml(t('w_chained', { n: n })));
            else if (k === 'no-position') items.push(escapeHtml(t('w_no_position', { n: NF0.format(+n) })));
            else if (k === 'bad-points') items.push(escapeHtml(t('w_bad_points', { n: NF0.format(+n) })));
        });
        doc.tracks.forEach(function (tr) {
            var d = derived(tr), sr = d.sr;
            if (sr.time) {
                var bad = 0; for (var i = 0; i < sr.n; i++) if (sr.time[i] < 631152000000) { bad++; break; }
                if (bad) items.push(escapeHtml(t('w_old_time', { name: tr.name || '' })));
            }
        });
        var tr = doc.tracks[active];
        if (tr && derived(tr).spikes.length) items.push(escapeHtml(t('w_spikes', { n: derived(tr).spikes.length })) + ' <button type="button" class="gt-link" data-goto="gtEdSpike">' + escapeHtml(t('fix_it')) + '</button>');
        box.innerHTML = items.map(function (x) { return '<p class="tc-note">• ' + x + '</p>'; }).join('');
        box.hidden = !items.length;
        box.querySelectorAll('[data-goto]').forEach(function (b) { b.addEventListener('click', function () { openEditTool(b.getAttribute('data-goto')); }); });
    }
    function openEditTool(id) {
        setTab('edit');
        var det = $(id);
        if (det) { det.open = true; try { det.querySelector('summary').focus({ preventScroll: true }); det.scrollIntoView({ block: 'nearest' }); } catch (_) {} }
    }

    // ── edit panel state ──────────────────────────────────────────────────
    function renderCursorState() {
        var has = !!doc && !!doc.tracks[active];
        var d = has ? derived(doc.tracks[active]) : null;
        var inside = has && cursorIdx != null && cursorIdx > 0 && cursorIdx < d.sr.n - 1;
        $('gtSplit').disabled = !inside;
        $('gtWptAdd').disabled = !(has && cursorIdx != null);
    }
    function renderEditState() {
        var has = !!doc && !!doc.tracks[active];
        var tr = has ? doc.tracks[active] : null;
        var d = has ? derived(tr) : null;
        renderCursorState();
        $('gtReverse').disabled = !has;
        $('gtMerge').disabled = !(doc && doc.tracks.length > 1);
        $('gtMergeRow').hidden = !(doc && doc.tracks.length > 1);
        $('gtJoinSegs').hidden = !(has && tr.segs.length > 1);
        $('gtUndo').disabled = !undo.length;
        $('gtRedo').disabled = !redo.length;
        // spikes
        var n = has ? d.spikes.length : 0;
        $('gtSpikeText').textContent = n ? t('spikes_found', { n: n }) : t('spikes_none');
        $('gtSpikeGo').disabled = !n;
        $('gtSpikeSub').textContent = n ? '· ' + n : '';
        // simplify
        var pts = has ? d.sr.n : 0;
        $('gtSimpCount').textContent = NF0.format(pts);
        $('gtSimpSub').textContent = pts ? '· ' + NF0.format(pts) : '';
        var rng = $('gtSimpRange');
        rng.max = String(Math.max(2, pts));
        rng.min = String(Math.min(50, pts));
        if (+rng.value > pts || !rng.dataset.touched) rng.value = String(Math.min(pts, pts > 10000 ? 10000 : Math.max(50, Math.round(pts / 4))));
        $('gtSimpTarget').textContent = NF0.format(+rng.value);
        $('gtSimpGo').disabled = !has || +rng.value >= pts;
        // time
        var timed = has && !!d.sr.time;
        $('gtTimeShift').hidden = !timed;
        $('gtTimeAdd').hidden = timed || !has;
        if (!timed && has) renderAddTime();
        // waypoints
        $('gtWptSub').textContent = doc && doc.waypoints.length ? '· ' + doc.waypoints.length : '';
        // track props — a file of waypoints only (or with every track removed) has no track to
        // name or colour: the fields went on holding the last track's name, and a rename then
        // reached for a track that is not there (Sentry TIMBRICA-FRONTEND-S3)
        var sw = $('gtColors');
        $('gtName').disabled = !has;
        sw.querySelectorAll('button').forEach(function (b) { b.disabled = !has; });
        if (has) {
            $('gtName').value = tr.name || '';
            sw.querySelectorAll('button').forEach(function (b) { b.classList.toggle('on', b.getAttribute('data-c') === tr.color); });
        } else {
            $('gtName').value = '';
        }
    }

    function renderAddTime() {
        var tr = doc && doc.tracks[active];
        if (!tr) return;
        var mode = guessMode(tr, derived(tr));
        document.querySelectorAll('#gtAddMode [data-m]').forEach(function (b) {
            var on = b.getAttribute('data-m') === mode;
            b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        var inp = $('gtAddSpeed'), want = String(Math.round(spd(mode) * (units === 'imperial' ? 2.2369363 : 3.6) * 10) / 10);
        if (document.activeElement !== inp) inp.value = want.replace('.', NF1.format(1.5).charAt(1));
        var est = estOf(tr, mode);
        $('gtAddEst').textContent = est ? t('addtime_est', { t: U.durShort(est.sec) }) : '';
    }

    // ── map gestures that leave the page its scroll ───────────────────────
    /* The map is half a phone screen inside a long page. One finger moving the
     * map means the page cannot be scrolled past it — so, as embedded Google
     * maps do, one finger scrolls the page, two fingers move and zoom the map;
     * with a mouse the wheel scrolls the page and Ctrl (⌘) + wheel zooms. In
     * full screen there is no page to scroll: the map takes every gesture. */
    var COARSE = false;
    try { COARSE = matchMedia('(pointer: coarse)').matches && !matchMedia('(pointer: fine)').matches; } catch (_) {}
    var gestHint = null, gestTimer = 0;
    function gestSay(text) {
        if (!gestHint) { gestHint = document.createElement('div'); gestHint.className = 'gt-gest'; gestHint.setAttribute('aria-hidden', 'true'); $('gtMapWrap').appendChild(gestHint); }
        gestHint.textContent = text;
        gestHint.classList.add('on');
        clearTimeout(gestTimer);
        gestTimer = setTimeout(function () { gestHint.classList.remove('on'); }, 1400);
    }
    function applyGestures() {
        if (!map) return;
        // full screen, or drawing a route: the map is the whole task, it takes every gesture
        var own = isFull() || (typeof Draw !== 'undefined' && Draw.on);
        if (COARSE) { if (own) map.dragging.enable(); else map.dragging.disable(); }
    }
    function bindGestures() {
        var wrap = $('gtMapWrap');
        if (COARSE) {
            var one = null;
            wrap.addEventListener('touchstart', function (e) { one = e.touches.length === 1 && !isFull() && !Draw.on ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null; }, { passive: true });
            wrap.addEventListener('touchmove', function (e) {
                if (!one || e.touches.length !== 1) return;
                var dx = e.touches[0].clientX - one.x, dy = e.touches[0].clientY - one.y;
                if (dx * dx + dy * dy > 400) { gestSay(t('gest_touch')); one = null; }
            }, { passive: true });
        } else {
            var mac = /Mac|iPhone|iPad/.test(navigator.platform || '');
            // capture on the wrapper: a plain wheel never reaches Leaflet, so the page scrolls
            wrap.addEventListener('wheel', function (e) {
                if (isFull() || Draw.on || e.ctrlKey || e.metaKey) return;
                if (!e.target.closest || !e.target.closest('#gtMap')) return;
                e.stopPropagation();
                if (Math.abs(e.deltaY) > 2) gestSay(t(mac ? 'gest_wheel_mac' : 'gest_wheel'));
            }, { capture: true, passive: true });
        }
    }

    // ── tabs ──────────────────────────────────────────────────────────────
    function setTab(name, focus) {
        if (['stats', 'parts', 'edit'].indexOf(name) < 0) name = 'stats';
        if (name === 'parts' && $('gtTabParts').hidden) name = 'stats';
        tab = name;
        document.querySelectorAll('#gtTabs [role=tab]').forEach(function (b) {
            var on = b.getAttribute('data-tab') === name;
            b.setAttribute('aria-selected', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
            if (on && focus) b.focus();
        });
        $('gtPanStats').hidden = name !== 'stats';
        $('gtPanParts').hidden = name !== 'parts';
        $('gtPanEdit').hidden = name !== 'edit';
        save('tab', name);
        if (name === 'parts' && doc) renderParts();
        var body = $('gtTabBody'); if (body) body.scrollTop = 0;
    }
    function bindTabs() {
        var tabs = Array.prototype.slice.call(document.querySelectorAll('#gtTabs [role=tab]'));
        tabs.forEach(function (b, i) {
            b.addEventListener('click', function () { setTab(b.getAttribute('data-tab')); ae('feature_use', { f: 'tab_' + b.getAttribute('data-tab') }); });
            b.addEventListener('keydown', function (e) {
                var vis = tabs.filter(function (x) { return !x.hidden; });
                var k = vis.indexOf(b), nx = -1;
                if (e.key === (RTL ? 'ArrowLeft' : 'ArrowRight')) nx = (k + 1) % vis.length;
                else if (e.key === (RTL ? 'ArrowRight' : 'ArrowLeft')) nx = (k - 1 + vis.length) % vis.length;
                else if (e.key === 'Home') nx = 0; else if (e.key === 'End') nx = vis.length - 1;
                if (nx < 0) return;
                e.preventDefault();
                setTab(vis[nx].getAttribute('data-tab'), true);
            });
        });
    }

    // ── render all ────────────────────────────────────────────────────────
    function render() {
        if (!doc) return;
        renderFiles();
        renderActive();
        renderSave();
        Draw.paint();
    }
    function renderActive() {
        if (!doc) return;
        var nameIn = $('gtFileName');
        if (nameIn && !nameIn.dataset.touched && doc.tracks[active]) nameIn.value = safeName(doc.tracks[active].name || doc.name || 'track');
        renderMap();
        renderStats();
        renderChart();
        renderSel();
        renderParts();
        renderNotes();
        renderEditState();
        renderColorBy();
    }
    function renderColorBy() {
        var sel = $('gtColorBy');
        var tr = doc && doc.tracks[active];
        var d = tr ? derived(tr) : null;
        var opts = [['file', t('cb_file')]];
        if (d && d.sr.time) opts.push(['speed', d.foot ? t('p_pace') : t('p_speed')]);
        if (d && d.sr.ele) { opts.push(['ele', t('p_ele')]); opts.push(['grade', t('p_grade')]); }
        if (d && d.sr.ch.hr) opts.push(['hr', t('p_hr')]);
        if (d && d.sr.ch.cad) opts.push(['cad', t('p_cad')]);
        if (d && d.sr.ch.power) opts.push(['power', t('p_power')]);
        if (d && d.sr.ch.temp) opts.push(['temp', t('p_temp')]);
        sel.innerHTML = '';
        opts.forEach(function (o) { var e = document.createElement('option'); e.value = o[0]; e.textContent = o[1]; sel.appendChild(e); });
        if (!opts.some(function (o) { return o[0] === colorBy; })) colorBy = 'file';
        sel.value = colorBy;
    }
    function renderFiles() {
        if (!doc) return;
        var box = $('gtFiles');
        box.innerHTML = '';
        doc.tracks.forEach(function (tr, i) {
            var b = document.createElement('div');
            b.className = 'gt-file' + (i === active ? ' on' : '') + (tr.visible === false ? ' off' : '');
            var eye = document.createElement('button');
            eye.type = 'button'; eye.className = 'gt-file-dot';
            eye.style.background = tr.color;
            eye.title = tr.visible === false ? t('show') : t('hide');
            eye.setAttribute('aria-label', eye.title);
            eye.setAttribute('aria-pressed', tr.visible === false ? 'false' : 'true');
            eye.addEventListener('click', function (e) { e.stopPropagation(); tr.visible = tr.visible === false; renderFiles(); renderMap(); });
            var nm = document.createElement('button');
            nm.type = 'button'; nm.className = 'gt-file-name';
            nm.dir = 'auto'; // a Latin name on an Arabic page truncates at ITS end
            nm.textContent = (tr.name || t('track_n', { n: i + 1 }));
            nm.title = (tr.srcName || '') + ' · ' + U.dist(derived(tr).stats.distance);
            if (i === active) nm.setAttribute('aria-current', 'true');
            nm.addEventListener('click', function () { active = i; selection = null; cursorIdx = null; renderFiles(); renderActive(); if (layer) layer.fit([i]); });
            var ds = document.createElement('span');
            ds.className = 'gt-file-d';
            ds.textContent = U.dist(derived(tr).stats.distance);
            var x = document.createElement('button');
            x.type = 'button'; x.className = 'gt-file-x';
            x.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
            x.title = t('remove_track'); x.setAttribute('aria-label', t('remove_track') + ': ' + (tr.name || t('track_n', { n: i + 1 })));
            x.addEventListener('click', function (e) { e.stopPropagation(); if (!doc) return; commit(E.removeTrack(doc, i), 'remove_track'); if (!doc.tracks.length && !doc.waypoints.length) closeAll(); });
            b.appendChild(eye); b.appendChild(nm); b.appendChild(ds); b.appendChild(x);
            box.appendChild(b);
        });
        if (!doc.tracks.length && doc.waypoints.length) {
            var w = document.createElement('div'); w.className = 'gt-file on';
            w.textContent = t('only_waypoints', { n: doc.waypoints.length });
            box.appendChild(w);
        }
    }

    function showWork() {
        $('gtWork').hidden = false;
        var alt = $('gtStartAlt'); if (alt) alt.hidden = true;
        initMap();
        render();
        setTab(tab);
        if (map) setTimeout(function () { map.invalidateSize(); }, 50);
    }
    function closeAll() {
        resetAll();
        hideWork();
    }
    // The workspace off the screen and the start back. The error box is left alone: an open
    // that gave nothing says why first (addDocs) and hides the workspace after (openFiles).
    function hideWork() {
        exitFull();
        $('gtWork').hidden = true;
        var alt = $('gtStartAlt'); if (alt) alt.hidden = false;
        if (layer) { layer.setTracks([]); layer.setWaypoints([]); layer.setMarks(null); }
        Recent.render();
    }

    // ── edits ─────────────────────────────────────────────────────────────
    function bindEdits() {
        $('gtSelCrop').addEventListener('click', function () { if (!selection || !doc) return; commit(E.crop(doc, active, selection.from, selection.to), 'crop'); fitActive(); });
        $('gtSelCut').addEventListener('click', function () { if (!selection || !doc) return; commit(E.cut(doc, active, selection.from, selection.to), 'cut'); });
        $('gtSelZoom').addEventListener('click', function () { if (selection && layer) layer.fitRange(active, selection.from, selection.to); });
        $('gtSelClear').addEventListener('click', function () { selection = null; chart.select(null); layer.setSelection(null); renderSel(); renderEditState(); });
        $('gtSplit').addEventListener('click', function () { if (cursorIdx == null || !doc) return; commit(E.split(doc, active, cursorIdx), 'split'); });
        $('gtReverse').addEventListener('click', function () { if (!doc) return; commit(E.reverse(doc, active), 'reverse'); });
        $('gtMerge').addEventListener('click', function () {
            if (!doc) return;
            var idx = doc.tracks.map(function (_, i) { return i; });
            commit(E.merge(doc, idx, $('gtMergeConnect').checked), 'merge');
            active = 0; render(); fitAll();
        });
        $('gtJoinSegs').addEventListener('click', function () { if (!doc) return; commit(E.joinSegments(doc, active), 'join_segments'); });
        $('gtSpikeGo').addEventListener('click', function () {
            var tr = doc && doc.tracks[active];
            if (!tr) return;
            var d = derived(tr);
            var n = d.spikes.length;
            commit(E.removePoints(doc, active, d.spikes), 'spikes');
            toast(t('spikes_removed', { n: n }), 'success');
        });
        var rng = $('gtSimpRange');
        rng.addEventListener('input', function () { rng.dataset.touched = '1'; $('gtSimpTarget').textContent = NF0.format(+rng.value); var tr = doc && doc.tracks[active]; $('gtSimpGo').disabled = !tr || +rng.value >= derived(tr).sr.n; });
        document.querySelectorAll('[data-simp]').forEach(function (b) {
            b.addEventListener('click', function () { rng.value = b.getAttribute('data-simp'); rng.dataset.touched = '1'; rng.dispatchEvent(new Event('input')); });
        });
        $('gtSimpGo').addEventListener('click', function () {
            if (!doc) return;
            var r = E.simplify(doc, active, +rng.value);
            commit(r.doc, 'simplify');
            toast(t('simp_done', { n: NF0.format(r.kept), m: U.ele(r.tol) }), 'success');
        });
        $('gtPrivGo').addEventListener('click', function () {
            if (!doc) return;
            var R = +$('gtPrivR').value;
            var next = doc;
            if (R > 0) next = E.privacyTrim(next, active, R);
            var what = {};
            if ($('gtPrivTime').checked) what.time = true;
            if ($('gtPrivSensors').checked) { what.hr = what.cad = what.power = what.temp = true; }
            if ($('gtPrivMeta').checked) what.meta = true;
            if (Object.keys(what).length) next = E.strip(next, what);
            if (next === doc) return;
            commit(next, 'privacy');
            toast(t('priv_done'), 'success');
        });
        $('gtPrivR').addEventListener('input', function () { $('gtPrivRVal').textContent = +this.value ? U.dist(+this.value) : t('off'); });
        $('gtShiftGo').addEventListener('click', function () {
            var h = parseFloat($('gtShiftH').value) || 0;
            if (!h || !doc) return;
            commit(E.shiftTime(doc, h * 3600000, active), 'time_shift');
        });
        $('gtAddTimeGo').addEventListener('click', function () {
            var tr = doc && doc.tracks[active];
            if (!tr) return;
            var v = $('gtAddStart').value;
            var start = v ? new Date(v).getTime() : Date.now();
            var mode = guessMode(tr, derived(tr));
            var est = estOf(tr, mode);
            if (!est) return;
            commit(E.setTimes(doc, active, start, est.t), 'add_time_' + mode);
            toast(t('addtime_done', { t: U.durShort(est.sec) }), 'success');
        });
        document.querySelectorAll('#gtAddMode [data-m]').forEach(function (b) {
            b.addEventListener('click', function () { timeMode = b.getAttribute('data-m'); save('addmode', timeMode); renderAddTime(); });
        });
        $('gtAddSpeed').addEventListener('input', function () {
            var tr = doc && doc.tracks[active];
            if (!tr) return;
            var v = parseFloat(String(this.value).replace(',', '.'));
            if (v > 0.5) { save('spd_' + guessMode(tr, derived(tr)), String(v)); renderAddTime(); renderStats(); }
        });
        $('gtWptAdd').addEventListener('click', function () {
            var tr = doc && doc.tracks[active];
            if (!tr || cursorIdx == null) return;
            var sr = derived(tr).sr, i = cursorIdx;
            var name = $('gtWptName').value.trim() || t('wpt_default', { n: doc.waypoints.length + 1 });
            commit(E.addWaypoint(doc, { lat: sr.lat[i], lon: sr.lon[i], ele: sr.ele && !isNaN(sr.ele[i]) ? sr.ele[i] : null, time: sr.time && !isNaN(sr.time[i]) ? sr.time[i] : null, name: name, desc: null, sym: null, type: null, src: tr.src }), 'wpt_add');
            $('gtWptName').value = '';
            toast(t('wpt_added', { name: name }), 'success');
        });
        $('gtName').addEventListener('change', function () { if (!doc || !doc.tracks[active]) return; commit(E.rename(doc, active, this.value.trim()), 'rename'); });
        $('gtColors').querySelectorAll('button').forEach(function (b) {
            b.addEventListener('click', function () { if (!doc || !doc.tracks[active]) return; commit(E.setColor(doc, active, b.getAttribute('data-c')), 'color'); });
        });
        $('gtUndo').addEventListener('click', doUndo);
        $('gtRedo').addEventListener('click', doRedo);
        document.addEventListener('keydown', function (e) {
            if (!doc || $('gtWork').hidden) return;
            var tag = (e.target && e.target.tagName) || '';
            if (/INPUT|TEXTAREA|SELECT/.test(tag)) return;
            var kl = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && kl === 'z') { e.preventDefault(); doUndo(); }
            else if ((e.ctrlKey || e.metaKey) && (kl === 'y' || (e.shiftKey && kl === 'z'))) { e.preventDefault(); doRedo(); }
        });
    }
    function fitActive() { if (layer) setTimeout(function () { layer.fit([active]); }, 20); }

    // ── terrain ───────────────────────────────────────────────────────────
    var demBusy = false;
    function runDem() {
        if (demBusy || !doc || !doc.tracks[active]) return;
        demBusy = true;
        // The lookup takes seconds. Its heights belong to THIS track of THIS document: an edit
        // or a replacing open in between makes them someone else's, and they are dropped.
        var at = doc, ai = active, tr = doc.tracks[ai], d = derived(tr);
        var btn = $('gtDemGo');
        if (btn) { btn.disabled = true; btn.textContent = t('ele_dem_busy', { p: 0 }); }
        var t0 = performance.now();
        GpsDem.sample(d.sr.lat, d.sr.lon, { urls: CFG.terrain, onProgress: function (p) { if (btn) btn.textContent = t('ele_dem_busy', { p: Math.round(p * 100) }); } })
            .then(function (res) {
                if (doc !== at) return;
                var next = E.setElevation(doc, ai, res.ele);
                next.tracks[ai]._eleBackup = true;
                commit(next, 'dem');
                ae('gt_dem', { ms: Math.round(performance.now() - t0), tiles: res.tiles, z: res.zoom, failed: res.failed, pts: d.sr.n });
            }, function (e) {
                toast(t('ele_dem_failed'), 'error');
                ae('tool_diagnostic', { issue: 'gt_dem_failed', err: String(e.message).slice(0, 60) });
                if (btn) { btn.disabled = false; btn.textContent = t('ele_dem_btn'); }
            }).then(function () { demBusy = false; });
    }

    // ── save ──────────────────────────────────────────────────────────────
    var TARGETS = {
        'gpx': { fmt: 'gpx', ext: 'gpx' },
        'gpx-rte': { fmt: 'gpx', ext: 'gpx', routes: true },
        'kml': { fmt: 'kml', ext: 'kml' },
        'kml-time': { fmt: 'kml', ext: 'kml', timed: true },
        'kmz': { fmt: 'kmz', ext: 'kmz', timed: true },
        'geojson': { fmt: 'geojson', ext: 'geojson' },
        'tcx': { fmt: 'tcx', ext: 'tcx', needTime: true },
        'tcx-course': { fmt: 'tcx', ext: 'tcx', course: true, single: true },
        'fit': { fmt: 'fit', ext: 'fit', needTime: true, single: true },
        'fit-course': { fmt: 'fit', ext: 'fit', course: true, single: true },
        'csv': { fmt: 'csv', ext: 'csv' },
        // destinations
        'strava': { fmt: 'gpx', ext: 'gpx', needTime: true, oneSeg: false },
        'garmin-connect': { fmt: 'fit', ext: 'fit', needTime: true, single: true },
        'garmin-course': { fmt: 'fit', ext: 'fit', course: true, single: true, maxPts: 10000 },
        'garmin-etrex': { fmt: 'gpx', ext: 'gpx', maxPts: 10000, oneSeg: true },
        'garmin-route': { fmt: 'gpx', ext: 'gpx', routes: true, maxPts: 250, single: true },
        'google-earth': { fmt: 'kmz', ext: 'kmz', timed: true },
        'google-mymaps': { fmt: 'kml', ext: 'kml', maxPts: 2000 },
        'komoot': { fmt: 'gpx', ext: 'gpx' },
        'osmand': { fmt: 'gpx', ext: 'gpx' },
        'organic': { fmt: 'gpx', ext: 'gpx' },
        'yandex': { fmt: 'geojson', ext: 'geojson', maxPts: 1000 },
        'wikiloc': { fmt: 'gpx', ext: 'gpx' }
    };
    var LOSSES = {
        gpx: [], kml: ['hr', 'cad', 'power', 'temp', 'time'], kmlTimed: [], kmz: [], geojson: [], tcx: ['temp'], fit: [], csv: [], gpxRte: ['hr', 'cad', 'power', 'temp']
    };
    var MIME = { gpx: 'application/gpx+xml', kml: 'application/vnd.google-earth.kml+xml', kmz: 'application/vnd.google-earth.kmz', geojson: 'application/geo+json', tcx: 'application/vnd.garmin.tcx+xml', fit: 'application/vnd.ant.fit', csv: 'text/csv' };

    function scopeVal() { var s = $('gtScope'); return doc && doc.tracks.length > 1 && s ? s.value : 'all'; }
    function renderSave() {
        if (!doc) return;
        var fmtSel = $('gtFmt');
        var key = fmtSel.value || load('fmt', 'gpx');
        if (!TARGETS[key]) key = 'gpx';
        fmtSel.value = key;
        var T = TARGETS[key];
        var tr = doc.tracks[active];
        var multi = doc.tracks.length > 1;
        var scope = $('gtScope');
        scope.hidden = !multi;
        scope.querySelector('option[value=all]').disabled = !!(T.single && multi);
        if (T.single && multi && scope.value === 'all') scope.value = 'one';
        // what will be written, what is dropped, what gets fixed
        var notes = [];
        var list = scopeTracks();
        var anyTime = list.some(function (x) { return x.segs.some(function (s) { return s.time; }); });
        var chans = {};
        list.forEach(function (x) { x.segs.forEach(function (s) { for (var c in s.ch) chans[c] = 1; }); });
        var lossKey = T.fmt === 'kml' || T.fmt === 'kmz' ? (T.timed ? 'kmlTimed' : 'kml') : (T.fmt === 'gpx' && T.routes ? 'gpxRte' : T.fmt);
        var lost = (LOSSES[lossKey] || []).filter(function (c) { return c === 'time' ? anyTime : chans[c]; });
        if (T.timed && !anyTime) lost = ['hr', 'cad', 'power', 'temp'].filter(function (c) { return chans[c]; });
        if (lost.length) notes.push({ cls: 'warn', text: t('save_loses', { what: lost.map(function (c) { return t('ch_' + c); }).join(', ') }) });
        if (T.needTime && !anyTime) notes.push({ cls: 'bad', text: t('save_need_time'), fix: 'gtEdTime' });
        if (T.course && !anyTime) notes.push({ cls: '', text: t('save_course_time') });
        var pts = list.reduce(function (a, x) { return a + derived(x).sr.n; }, 0);
        if (T.maxPts && pts > T.maxPts) notes.push({ cls: '', text: t('save_simplify', { n: NF0.format(pts), m: NF0.format(T.maxPts) }) });
        if (T.oneSeg && list.some(function (x) { return x.segs.length > 1; })) notes.push({ cls: '', text: t('save_join') });
        var tip = S['dest_' + key];
        var box = $('gtSaveNotes');
        var bad = notes.filter(function (n) { return n.cls === 'bad'; });
        if (bad.length) {
            // the file cannot be made as asked: say why, and put the fix one press away
            box.innerHTML = bad.map(function (n) { return '<p class="tc-msg bad"><span class="gt-nt">' + escapeHtml(n.text) + '</span>' + (n.fix ? ' <button type="button" class="gt-link" data-fix="' + n.fix + '">' + escapeHtml(t('fix_' + n.fix)) + '</button>' : '') + '</p>'; }).join('');
            box.querySelectorAll('[data-fix]').forEach(function (b) { b.addEventListener('click', function () { openEditTool(b.getAttribute('data-fix')); }); });
        } else {
            box.innerHTML = (tip ? '<p class="tc-msg good clamp" tabindex="0" role="button" aria-expanded="false"><span class="gt-nt">' + escapeHtml(tip) + '</span></p>' : '')
                + notes.map(function (n) { return '<p class="gt-ln ' + n.cls + '">' + escapeHtml(n.text) + '</p>'; }).join('');
        }
        // the destination's instructions: two lines, the rest on a press
        var tipEl = box.querySelector('.clamp');
        if (tipEl) {
            var open = function () { var on = tipEl.classList.toggle('clamp'); tipEl.setAttribute('aria-expanded', on ? 'false' : 'true'); };
            tipEl.addEventListener('click', open);
            tipEl.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
            requestAnimationFrame(function () { var sp = tipEl.firstChild; if (sp && sp.scrollHeight <= sp.clientHeight + 2) { tipEl.classList.remove('clamp'); tipEl.removeAttribute('role'); tipEl.removeAttribute('tabindex'); tipEl.removeAttribute('aria-expanded'); } });
        }
        $('gtDownload').disabled = (T.needTime && !anyTime) || !list.length;
        var base = safeName((tr && tr.name) || doc.name || 'track');
        var nameIn = $('gtFileName');
        if (!nameIn.dataset.touched) nameIn.value = base;
        $('gtFileExt').textContent = '.' + T.ext;
        renderSend(T);
    }
    function scopeTracks() {
        if (!doc) return [];
        var multi = doc.tracks.length > 1;
        if (!multi) return doc.tracks.slice();
        var T = TARGETS[$('gtFmt').value || 'gpx'] || {};
        if (scopeVal() === 'one' || T.single) return doc.tracks[active] ? [doc.tracks[active]] : [];
        return doc.tracks.filter(function (x) { return x.visible !== false; });
    }
    function safeName(s) {
        if (window.__outputName && window.__outputName.sanitize) { try { return window.__outputName.sanitize(String(s)) || 'track'; } catch (_) {} }
        return String(s).replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').trim().slice(0, 80) || 'track';
    }

    function prepareDoc(list, T, src) {
        src = src || doc;
        if (T.routes) list = list.map(function (tr) { return Draw.vertices(tr); });
        var d = { format: src.format, name: src.name, creator: null, device: src.device, time: src.time, tracks: list, waypoints: src.waypoints || [], areas: src.areas || [], warnings: [] };
        // fixes the destination needs, applied on a COPY: the page keeps the original
        d.tracks.forEach(function (_, i) {
            var tr = d.tracks[i];
            if (T.oneSeg && tr.segs.length > 1) d = E.joinSegments(d, i);
        });
        if (T.maxPts) {
            var total = d.tracks.reduce(function (a, x) { return a + x.segs.reduce(function (b, s) { return b + s.n; }, 0); }, 0);
            if (total > T.maxPts) d.tracks.forEach(function (x, i) {
                var n = x.segs.reduce(function (b, s) { return b + s.n; }, 0);
                var share = Math.max(2, Math.floor(T.maxPts * n / total));
                if (n > share) d = E.simplify(d, i, share).doc;
            });
        }
        return d;
    }

    function exportBytes(key, list, src) {
        var T = TARGETS[key];
        var d = prepareDoc(list, T, src);
        var creator = (CFG.brand || 'Timbrica') + ' — ' + location.host;
        switch (T.fmt) {
            case 'gpx': return W.gpx(d, { routes: T.routes || false, creator: creator });
            case 'kml': return W.kml(d, { timed: !!T.timed });
            case 'kmz': {
                var kml = W.kml(d, { timed: !!T.timed });
                return window.fflate.zipSync({ 'doc.kml': new TextEncoder().encode(kml) }, { level: 6 });
            }
            case 'geojson': return W.geojson(d);
            case 'tcx': return W.tcx(d, { course: !!T.course });
            case 'fit': return W.fit(d, { course: !!T.course, track: 0 });
            case 'csv': return W.csv(d);
        }
        throw new Error('fmt');
    }

    function ensureFflate() {
        if (window.fflate) return Promise.resolve();
        return new Promise(function (res, rej) {
            var s = document.createElement('script');
            s.src = CFG.fflateUrl; s.onload = function () { res(); }; s.onerror = function () { rej(new Error('fflate')); };
            document.head.appendChild(s);
        });
    }

    function download() {
        if (!doc) return;
        var key = $('gtFmt').value;
        var T = TARGETS[key];
        var list = scopeTracks();
        if (!list.length) return;
        var separate = doc.tracks.length > 1 && scopeVal() === 'each' && !T.single;
        var keys = [];
        list.forEach(function (x) { if (keys.indexOf(x.src || 'x') < 0) keys.push(x.src || 'x'); });
        var run = window.__toolLimit ? window.__toolLimit.begin(SLUG, keys) : { commit: function () {}, abort: function () {} };
        if (!run) return;
        var base = safeName($('gtFileName').value || 'track');
        var need = T.fmt === 'kmz' ? ensureFflate() : Promise.resolve();
        need.then(function () {
            if (separate) {
                var items = list.map(function (x, i) {
                    var bytes = exportBytes(key, [x]);
                    return { name: safeName(x.name || base + '-' + (i + 1)) + '.' + T.ext, blob: new Blob([bytes], { type: MIME[T.fmt] }) };
                });
                var zipBase = safeName(doc.name || (doc.tracks.length > 1 ? t('zip_name') : base));
                if (window.__batchDeliver && window.__batchDeliver.deliver) return window.__batchDeliver.deliver(items, { zipName: zipBase + '.zip', slug: SLUG });
                return window.__downloadFile(items[0].blob, items[0].name);
            }
            var bytes = exportBytes(key, list);
            return window.__downloadFile(new Blob([bytes], { type: MIME[T.fmt] }), base + '.' + T.ext);
        }).then(function (ok) {
            if (ok === false) { run.abort(); return; }
            run.commit();
            var pts = list.reduce(function (a, x) { return a + derived(x).sr.n; }, 0);
            try { if (window.__aeToolComplete) window.__aeToolComplete({ fmt: key, from: doc.format, tracks: list.length, pts: pts, sep: separate ? 1 : 0 }); } catch (_) {}
            save('fmt', key);
        }, function (e) {
            run.abort();
            toast(t('save_failed'), 'error');
            ae('tool_diagnostic', { issue: 'gt_export_failed', fmt: key, err: String(e && e.message || e).slice(0, 80) });
        });
    }

    /* «Send to an app» on a phone: the system share sheet with the FILE, so a
     * GPX goes straight into OsmAnd, Organic Maps or a messenger instead of the
     * Downloads folder. Shown only where the browser says it can share this
     * very file type — Chrome on Android shares no .gpx at all (its share
     * allowlist is images, audio, video, text and PDF), iOS Safari shares any. */
    var sendProbe = {};
    function canSendFile(ext, mime) {
        if (!navigator.canShare || !navigator.share || typeof File === 'undefined') return false;
        var k = ext + '|' + mime;
        if (sendProbe[k] != null) return sendProbe[k];
        try { sendProbe[k] = !!navigator.canShare({ files: [new File(['x'], 'track.' + ext, { type: mime })] }); } catch (_) { sendProbe[k] = false; }
        return sendProbe[k];
    }
    function renderSend(T) {
        var b = $('gtSend');
        if (!b) return;
        b.hidden = !(T && doc && canSendFile(T.ext, MIME[T.fmt]) && doc.tracks.length && scopeVal() !== 'each');
    }
    function sendFile() {
        if (!doc) return;
        var key = $('gtFmt').value, T = TARGETS[key];
        var list = scopeTracks();
        if (!list.length) return;
        var keys = [];
        list.forEach(function (x) { if (keys.indexOf(x.src || 'x') < 0) keys.push(x.src || 'x'); });
        var run = window.__toolLimit ? window.__toolLimit.begin(SLUG, keys) : { commit: function () {}, abort: function () {} };
        if (!run) return;
        var name = safeName($('gtFileName').value || 'track') + '.' + T.ext;
        // the share sheet must open inside this click: no await before share()
        if (T.fmt === 'kmz' && !window.fflate) { run.abort(); ensureFflate(); toast(t('send_retry'), 'info'); return; }
        var file;
        try { file = new File([exportBytes(key, list)], name, { type: MIME[T.fmt] }); } catch (e) { run.abort(); toast(t('save_failed'), 'error'); return; }
        navigator.share({ files: [file], title: name }).then(function () {
            run.commit();
            try { if (window.__aeToolComplete) window.__aeToolComplete({ fmt: key, from: doc.format, tracks: list.length, via: 'share' }); } catch (_) {}
        }, function (e) {
            run.abort();
            if (e && e.name !== 'AbortError') { toast(t('save_failed'), 'error'); ae('tool_diagnostic', { issue: 'gt_share_failed', err: String(e.message || e).slice(0, 80) }); }
        });
    }

    // ── archive browser ───────────────────────────────────────────────────
    var arcFile = null;
    function showArchive(res, file) {
        arcFile = file;
        archive = { id: res.id, listing: res.listing, stack: [] };
        var af = $('gtArcFmt');
        if (af && !af.options.length) { af.innerHTML = $('gtFmt').innerHTML; af.value = load('arcfmt', 'gpx'); if (!af.value) af.value = 'gpx'; }
        renderArchive();
        var dlg = $('gtArc');
        if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
        ae('gt_archive', { kind: res.listing.kind, n: res.listing.entries.length, mb: Math.round(file.size / 1e6) });
    }
    function renderArchive() {
        var Lst = archive.listing, q = ($('gtArcSearch').value || '').toLowerCase();
        $('gtArcTitle').textContent = t('arc_title_' + Lst.kind) !== 'arc_title_' + Lst.kind ? t('arc_title_' + Lst.kind) : t('arc_title_generic');
        $('gtArcCount').textContent = t('arc_count', { n: NF0.format(Lst.entries.length) });
        var body = $('gtArcList');
        var rows = Lst.entries.filter(function (e) {
            if (!q) return true;
            return (String(e.name || '') + ' ' + e.path + ' ' + String(e.sport || '')).toLowerCase().indexOf(q) >= 0;
        }).slice(0, 3000);
        body.innerHTML = rows.map(function (e) {
            var dt = e.date ? new Intl.DateTimeFormat(LOC, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(e.date)) : '';
            var label = e.name || e.path.split('/').pop();
            return '<label class="gt-arc-row"><input type="checkbox" value="' + e.id + '"' + (e.fmt === 'zip' ? ' data-zip="1"' : '') + '><span class="gt-arc-n">' + escapeHtml(label) + '<small>' + escapeHtml([dt, e.sport, e.fmt.toUpperCase(), fmtBytes(e.size)].filter(Boolean).join(' · ')) + '</small></span></label>';
        }).join('') || '<p class="tc-hint">' + escapeHtml(t('arc_empty')) + '</p>';
        body.querySelectorAll('input').forEach(function (i) { i.addEventListener('change', arcCount); });
        arcCount();
        $('gtArcHeat').disabled = !Lst.entries.some(function (e) { return e.fmt !== 'zip'; });
    }
    function arcCount() {
        var n = $('gtArcList').querySelectorAll('input:checked').length;
        $('gtArcOpen').disabled = !n || !!arcRun;
        $('gtArcOpen').textContent = n ? t('arc_open_n', { n: n }) : t('arc_open');
        var m = arcPicked().length, sv = $('gtArcSave');
        sv.disabled = !m || !!arcRun;
        sv.querySelector('span').textContent = m ? t('arc_save_n', { n: NF0.format(m) }) : t('arc_save_btn');
    }
    function fmtBytes(b) { if (!b) return ''; return b > 1e6 ? NF1.format(b / 1e6) + ' MB' : NF0.format(b / 1e3) + ' KB'; }
    function arcOpenSelected() {
        var ids = Array.prototype.map.call($('gtArcList').querySelectorAll('input:checked'), function (i) { return +i.value; });
        if (!ids.length) return;
        if (ids.length > 30) { toast(t('arc_too_many', { n: 30 }), 'warning'); ids = ids.slice(0, 30); }
        setBusy(true, t('busy_reading'));
        call({ op: 'read', arc: archive.id, ids: ids }).then(function (res) {
            setBusy(false);
            var docs = [], nested = null;
            res.docs.forEach(function (x) {
                if (x.listing) nested = x;
                else if (x.doc) { tagDoc(x.doc, arcFile); x.doc.tracks.forEach(function (tr) { tr.src = fileKey(arcFile) + '#' + x.id; }); docs.push(x.doc); }
            });
            if (nested) { archive.stack.push(archive.listing); archive.id = nested.archive; archive.listing = nested.listing; renderArchive(); return; }
            closeArc();
            if (docs.length) { heatOff(); addDocs(docs, !!doc); }
        }, function (e) { setBusy(false); toast(errText(e.message), 'error'); });
    }
    function closeArc() { if (arcRun) return; var dlg = $('gtArc'); if (dlg.close) dlg.close(); else dlg.removeAttribute('open'); }

    /* Convert a selection of the archive in one go — a year of rides from a
     * Strava export as GPX for another app. Each activity is read, written and
     * handed to the folder (or the zip) one at a time, so a thousand files
     * never sit in memory together. Every activity is one unit of the daily
     * limit, keyed by the archive AND the entry: the same ride saved again
     * today is free, as in the main lane. */
    var arcRun = null;
    function arcPicked() {
        return Array.prototype.filter.call($('gtArcList').querySelectorAll('input:checked'), function (i) { return !i.hasAttribute('data-zip'); })
            .map(function (i) { return +i.value; });
    }
    function arcSaveSelected() {
        if (arcRun) return;
        var ids = arcPicked();
        if (!ids.length || !archive) return;
        var key = $('gtArcFmt').value, T = TARGETS[key];
        if (!T) return;
        var base = fileKey(arcFile);
        var run = window.__toolLimit ? window.__toolLimit.begin(SLUG, ids.map(function (id) { return base + '#' + id; })) : { commit: function () {}, abort: function () {} };
        if (!run) return;
        if (!window.__batchDeliver) { run.abort(); toast(t('save_failed'), 'error'); return; }
        var title = safeName($('gtArcTitle').textContent || 'tracks');
        // the folder picker lives on this click's activation: it is the first await
        var batchP = window.__batchDeliver.openBatch({ zipName: title + '-' + T.ext + '.zip', slug: SLUG, total: ids.length });
        var state = arcRun = { stop: false, done: 0, skipped: 0, total: ids.length };
        var used = new Set();
        var t0 = performance.now();
        arcProgress();
        var batch = null;
        batchP.then(function (b) {
            batch = b;
            return T.fmt === 'kmz' ? ensureFflate() : null;
        }).then(function () {
            var chunks = [];
            for (var i = 0; i < ids.length; i += 4) chunks.push(ids.slice(i, i + 4));
            return chunks.reduce(function (p, ch) {
                return p.then(function () {
                    if (state.stop) return;
                    return call({ op: 'read', arc: archive.id, ids: ch }).then(function (res) {
                        return res.docs.reduce(function (q, x) {
                            return q.then(function () {
                                if (state.stop) return;
                                var d = x.doc;
                                var list = d ? d.tracks.filter(function (tr) { return tr.segs.some(function (s) { return s.n > 1; }); }) : [];
                                var timed = list.some(function (tr) { return tr.segs.some(function (s) { return s.time; }); });
                                if (!list.length || (T.needTime && !timed)) { state.skipped++; state.done++; arcProgress(); return; }
                                if (T.single) list = list.slice(0, 1);
                                var bytes = exportBytes(key, list, d);
                                var nm = safeName(d.name || (list[0] && list[0].name) || ('track-' + x.id)) + '.' + T.ext;
                                nm = window.__outputName && window.__outputName.distinct ? window.__outputName.distinct(nm, used) : (used.has(nm) ? x.id + '-' + nm : nm);
                                used.add(nm);
                                return batch.write(nm, new Blob([bytes], { type: MIME[T.fmt] })).then(function () { state.done++; arcProgress(); });
                            });
                        }, Promise.resolve());
                    });
                });
            }, Promise.resolve());
        }).then(function () {
            if (state.stop) { if (batch && batch.abort) batch.abort(); return { via: 'stopped', written: 0 }; }
            return batch.finish();
        }).then(function (res) {
            var wrote = res && res.written || 0;
            if (!wrote || res.via === 'refused' || res.via === 'stopped') run.abort(); else run.commit();
            ae('gt_batch', { n: ids.length, wrote: wrote, skipped: state.skipped, fmt: key, via: res && res.via, ms: Math.round(performance.now() - t0) });
            if (wrote) {
                toast(t('arc_saved', { n: NF0.format(wrote) }) + (state.skipped ? ' ' + t('arc_skipped', { n: NF0.format(state.skipped) }) : ''), 'success');
                try { if (window.__aeToolComplete) window.__aeToolComplete({ fmt: key, from: 'archive', tracks: wrote, batch: 1 }); } catch (_) {}
            } else if (state.skipped && !state.stop) toast(t('arc_none_saved'), 'warning');
        }, function (e) {
            run.abort();
            toast(t('save_failed'), 'error');
            ae('tool_diagnostic', { issue: 'gt_batch_failed', fmt: key, err: String(e && e.message || e).slice(0, 80) });
        }).then(function () { arcRun = null; arcProgress(); });
    }
    function arcProgress() {
        var box = $('gtArcProg');
        if (!box) return;
        box.hidden = !arcRun;
        ['gtArcSave', 'gtArcOpen', 'gtArcHeat', 'gtArcClose'].forEach(function (id) { var b = $(id); if (b) b.disabled = !!arcRun || (id === 'gtArcSave' || id === 'gtArcOpen') && !$('gtArcList').querySelectorAll('input:checked').length; });
        if (arcRun) $('gtArcProgText').textContent = t('arc_progress', { d: NF0.format(arcRun.done), n: NF0.format(arcRun.total) });
    }

    // ── heat map ──────────────────────────────────────────────────────────
    var heatReq = 0;
    function arcHeat() {
        var ids = archive.listing.entries.filter(function (e) { return e.fmt !== 'zip'; }).map(function (e) { return e.id; });
        closeArc();
        heatLines = [];
        heatOn = true;
        doc = doc || { format: 'heat', tracks: [], waypoints: [], areas: [], warnings: [] };
        showWork();
        $('gtHeatBar').hidden = false;
        $('gtHeatText').textContent = t('heat_progress', { d: 0, n: ids.length });
        heatReq = ++reqId;
        getWorker().postMessage({ op: 'heat', id: heatReq, arc: archive.id, ids: ids });
        layer.setHeat({ lines: heatLines });
        ae('feature_use', { f: 'heatmap', n: ids.length });
    }
    var heatFitted = false;
    function onHeat(m) {
        if (m.id !== heatReq) return;
        if (m.kind === 'heat') {
            m.part.forEach(function (p) { heatLines.push(p); });
            $('gtHeatText').textContent = t('heat_progress', { d: m.done, n: m.total });
            layer.setHeat({ lines: heatLines });
            if (!heatFitted && heatLines.length >= 5) { heatFitted = true; layer.fit(); }
        } else {
            var dist = heatLines.reduce(function (a, l) { return a + (l.dist || 0); }, 0);
            $('gtHeatText').textContent = t('heat_done', { n: NF0.format(heatLines.length), d: U.dist(dist) });
            if (!heatFitted) layer.fit();
        }
    }
    function heatOff() {
        if (!heatOn) return;
        heatOn = false; heatLines = []; heatFitted = false;
        if (layer) layer.setHeat(null);
        var b = $('gtHeatBar'); if (b) b.hidden = true;
    }
    function openHeatEntry(line) {
        setBusy(true, t('busy_reading'));
        call({ op: 'read', arc: archive.id, ids: [line.entry] }).then(function (res) {
            setBusy(false);
            var x = res.docs[0];
            if (!x || !x.doc) return;
            tagDoc(x.doc, arcFile);
            heatOff();
            addDocs([x.doc], false);
        }, function () { setBusy(false); });
    }

    // ── sample ────────────────────────────────────────────────────────────
    function injectFile(f, source) {
        var zone = $('gtUpload');
        openSource = source;
        // through the shared uploader: the zone collapses to the same
        // «file · another file · ×» row as for a picked file
        if (zone && typeof zone.__uploaderInjectFile === 'function') { zone.__uploaderInjectFile(f, source); return; }
        openFiles([f], 'replace');
    }
    function openSample() {
        warmWorker();
        setBusy(true, t('busy_reading'));
        fetch(CFG.sampleUrl).then(function (r) { if (!r.ok) throw new Error('sample'); return r.blob(); }).then(function (b) {
            var f = new File([b], CFG.sampleName || 'sample.gpx', { type: 'application/gpx+xml', lastModified: 1 });
            setBusy(false);
            injectFile(f, 'sample');
        }).catch(function () { setBusy(false); toast(t('err_generic'), 'error'); });
        ae('feature_use', { f: 'sample' });
    }

    // ── recent files (this browser only) ──────────────────────────────────
    /* The last few opened files are kept in this browser's IndexedDB so that
     * the next visit can reopen them with one press. Nothing is sent anywhere
     * for this; a person can forget each one. */
    var Recent = (function () {
        var DB = 'timbrica-gpx-recent', ST = 'files', MAX_N = 8, MAX_FILE = 20 * 1024 * 1024, MAX_ALL = 60 * 1024 * 1024;
        var dbp = null;
        function db() {
            if (dbp) return dbp;
            dbp = new Promise(function (res, rej) {
                if (!window.indexedDB) { rej(new Error('no-idb')); return; }
                var rq;
                try { rq = indexedDB.open(DB, 1); } catch (e) { rej(e); return; }
                rq.onupgradeneeded = function () { try { rq.result.createObjectStore(ST, { keyPath: 'k' }); } catch (_) {} };
                rq.onsuccess = function () { res(rq.result); };
                rq.onerror = function () { rej(rq.error); };
            });
            dbp.catch(function () {});
            return dbp;
        }
        function tx(mode, fn) {
            return db().then(function (d) {
                return new Promise(function (res, rej) {
                    var x = d.transaction(ST, mode), st = x.objectStore(ST), out = fn(st);
                    x.oncomplete = function () { res(out && out.result !== undefined ? out.result : out); };
                    x.onerror = function () { rej(x.error); };
                    x.onabort = function () { rej(x.error); };
                });
            });
        }
        function list() { return tx('readonly', function (st) { return st.getAll(); }).then(function (a) { return (a || []).sort(function (p, q) { return q.t - p.t; }); }).catch(function () { return []; }); }
        function add(f, d) {
            if (!f || !f.size || f.size > MAX_FILE) return;
            var tr = d.tracks[0];
            var meta = { k: fileKey(f), name: f.name, size: f.size, lm: f.lastModified || 0, t: Date.now(), fmt: d.format || '', dist: tr ? derived(tr).stats.distance : 0, n: d.tracks.length, blob: f };
            list().then(function (all) {
                return tx('readwrite', function (st) {
                    st.put(meta);
                    var keep = [meta.k], total = meta.size;
                    all.forEach(function (r) {
                        if (r.k === meta.k) return;
                        if (keep.length < MAX_N && total + r.size <= MAX_ALL) { keep.push(r.k); total += r.size; }
                        else st.delete(r.k);
                    });
                });
            }).catch(function () {});
        }
        function del(k) { return tx('readwrite', function (st) { st.delete(k); }).catch(function () {}); }
        function get(k) { return tx('readonly', function (st) { return st.get(k); }).catch(function () { return null; }); }
        function render() {
            var box = $('gtRecent'), ul = $('gtRecentList');
            if (!box || !ul) return;
            list().then(function (all) {
                ul.innerHTML = '';
                all.forEach(function (r) {
                    var li = document.createElement('li'); li.className = 'gt-recent-i';
                    var o = document.createElement('button'); o.type = 'button'; o.className = 'gt-recent-o';
                    var b = document.createElement('b'); b.dir = 'auto'; b.textContent = r.name;
                    var sm = document.createElement('small');
                    var when = '';
                    try { when = new Intl.DateTimeFormat(LOC, { day: 'numeric', month: 'short' }).format(new Date(r.t)); } catch (_) {}
                    sm.textContent = [r.dist ? U.dist(r.dist) : '', String(r.fmt || '').toUpperCase(), when].filter(Boolean).join(' · ');
                    o.appendChild(b); o.appendChild(sm);
                    o.addEventListener('click', function () {
                        get(r.k).then(function (rec) {
                            if (!rec || !rec.blob) { del(r.k); render(); return; }
                            var f = rec.blob instanceof File ? rec.blob : new File([rec.blob], rec.name, { lastModified: rec.lm });
                            warmWorker();
                            injectFile(f, 'recent');
                            ae('feature_use', { f: 'recent_open' });
                        });
                    });
                    var x = document.createElement('button'); x.type = 'button'; x.className = 'gt-ib';
                    x.title = t('recent_forget'); x.setAttribute('aria-label', t('recent_forget') + ': ' + r.name);
                    x.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
                    x.addEventListener('click', function () { del(r.k).then(render); });
                    li.appendChild(o); li.appendChild(x);
                    ul.appendChild(li);
                });
                box.hidden = !all.length;
            });
        }
        return { add: add, render: render, list: list };
    })();

    // ── fullscreen ────────────────────────────────────────────────────────
    function isFull() { var f = $('gtWork'); return document.fullscreenElement === f || f.classList.contains('gt-max'); }
    function paintFull() {
        var on = isFull();
        var b = $('gtFull');
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.title = on ? t('fullscreen_exit') : t('fullscreen');
        b.setAttribute('aria-label', b.title);
        b.querySelector('.gt-full-on').hidden = on;
        b.querySelector('.gt-full-off').hidden = !on;
        applyGestures();
        if (map) setTimeout(function () { map.invalidateSize(); }, 60);
    }
    function exitFull() {
        var f = $('gtWork');
        if (document.fullscreenElement === f && document.exitFullscreen) { document.exitFullscreen().catch(function () {}); }
        if (f.classList.contains('gt-max')) { f.classList.remove('gt-max'); document.documentElement.style.overflow = ''; }
        paintFull();
    }
    function toggleFull() {
        var f = $('gtWork');
        if (isFull()) { exitFull(); return; }
        var fallback = function () { f.classList.add('gt-max'); document.documentElement.style.overflow = 'hidden'; paintFull(); };
        if (f.requestFullscreen && document.fullscreenEnabled !== false) {
            f.requestFullscreen({ navigationUI: 'hide' }).then(paintFull, fallback);
        } else fallback();
        ae('feature_use', { f: 'fullscreen' });
    }

    // ── layers popover ────────────────────────────────────────────────────
    function bindLayers() {
        var btn = $('gtLayersBtn'), pop = $('gtLayersPop');
        if (!btn || !pop) return;
        // placement, following the button, light dismiss and the browsers
        // without the Popover API belong to the shared anchored-popover.js;
        // here only «show the current state when it opens»
        if (window.MutationObserver) new MutationObserver(function () {
            if (pop.getAttribute('data-pop-open') === 'true') syncLayerControls();
        }).observe(pop, { attributes: true, attributeFilter: ['data-pop-open'] });
        if (typeof L !== 'undefined' && L.DomEvent) { L.DomEvent.disableClickPropagation(pop); L.DomEvent.disableScrollPropagation(pop); }
        document.querySelectorAll('input[name=gtBase]').forEach(function (r) {
            r.addEventListener('change', function () { if (r.checked) applyBase(r.value); });
        });
        $('gtRelief').addEventListener('change', function () { setRelief(this.checked); });
        $('gtKm').addEventListener('change', function () { save('km', this.checked ? '1' : '0'); renderMap(); });
        $('gtClimbMarks').addEventListener('change', function () { marksOn = this.checked; save('cmarks', marksOn ? '1' : '0'); renderMapMarks(); });
        syncLayerControls();
    }

    // ── wiring ────────────────────────────────────────────────────────────
    var addMode = false;
    function bindUploader() {
        var zone = $('gtUpload');
        if (!zone) return;
        var onFiles = function (files) { openFiles(files, addMode ? 'add' : 'replace'); addMode = false; };
        // the shared binder calls back with (file, detail); a multi-pick is detail.files
        if (window.__bindUploaderFile) window.__bindUploaderFile(zone, function (f, d) { onFiles(d && d.files && d.files.length ? d.files : [f]); });
        else zone.addEventListener('tool-uploader:file', function (e) { var d = e.detail || {}; onFiles(d.files || [d.file]); });
        zone.addEventListener('tool-uploader:clear', function () { closeAll(); });
        ['pointerenter', 'dragenter', 'focusin', 'touchstart'].forEach(function (ev) { zone.addEventListener(ev, warmWorker, { once: true, passive: true }); });
        document.addEventListener('dragenter', warmWorker, { once: true });
        $('gtAdd').addEventListener('click', function () {
            var inp = zone.querySelector('input[type=file]');
            if (!inp) return;
            addMode = true;
            inp.click();
        });
    }

    function bindUi() {
        $('gtSample').addEventListener('click', openSample);
        $('gtColorBy').addEventListener('change', function () { colorBy = this.value; save('colorby', colorBy); renderMap(); ae('feature_use', { f: 'color_' + colorBy }); });
        $('gtFit').addEventListener('click', function () { if (layer) layer.fit(); });
        $('gtXDist').addEventListener('click', function () { xKind = 'dist'; save('xkind', xKind); renderChart(); });
        $('gtXTime').addEventListener('click', function () { xKind = 'time'; save('xkind', xKind); renderChart(); });
        $('gtShade').addEventListener('click', function () { shadeOn = !shadeOn; save('shade', shadeOn ? '1' : '0'); renderChart(); ae('feature_use', { f: 'shade', on: shadeOn ? 1 : 0 }); });
        var sm = $('gtSelMode');
        sm.addEventListener('click', function () { var on = !sm.classList.contains('on'); sm.classList.toggle('on', on); sm.setAttribute('aria-pressed', on ? 'true' : 'false'); chart.selectMode(on); });
        document.querySelectorAll('#gtUnits [data-u]').forEach(function (b) {
            b.classList.toggle('on', b.getAttribute('data-u') === units);
            b.setAttribute('aria-pressed', b.getAttribute('data-u') === units ? 'true' : 'false');
            b.addEventListener('click', function () {
                units = b.getAttribute('data-u'); save('units', units);
                document.querySelectorAll('#gtUnits [data-u]').forEach(function (x) { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
                if (doc) { doc.tracks.forEach(function (tr) { var d = cache.get(tr); if (d) { d.best = null; d.est = null; } }); render(); }
            });
        });
        $('gtFmt').addEventListener('change', function () { save('fmt', this.value); renderSave(); ae('feature_use', { f: 'fmt_' + this.value }); });
        $('gtScope').addEventListener('change', renderSave);
        $('gtFileName').addEventListener('input', function () { this.dataset.touched = '1'; });
        $('gtDownload').addEventListener('click', download);
        $('gtSend').addEventListener('click', sendFile);
        $('gtArcSearch').addEventListener('input', renderArchive);
        $('gtArcAll').addEventListener('click', function () {
            var boxes = $('gtArcList').querySelectorAll('input');
            var all = Array.prototype.every.call(boxes, function (b) { return b.checked; });
            boxes.forEach(function (b) { b.checked = !all; });
            arcCount();
        });
        $('gtArcOpen').addEventListener('click', arcOpenSelected);
        $('gtArcSave').addEventListener('click', arcSaveSelected);
        $('gtArcStop').addEventListener('click', function () { if (arcRun) arcRun.stop = true; });
        $('gtArcFmt').addEventListener('change', function () { save('arcfmt', this.value); });
        $('gtArc').addEventListener('cancel', function (e) { if (arcRun) e.preventDefault(); });
        $('gtArcHeat').addEventListener('click', arcHeat);
        $('gtArcClose').addEventListener('click', closeArc);
        $('gtHeatClose').addEventListener('click', function () { heatOff(); if (doc && doc.tracks.length) render(); else closeAll(); });
        $('gtShare').addEventListener('click', shareLink);
        $('gtImage').addEventListener('click', function () { exportPicture('png'); });
        $('gtPdf').addEventListener('click', function () { exportPicture('pdf'); });
        $('gtFull').addEventListener('click', toggleFull);
        document.addEventListener('fullscreenchange', paintFull);
        document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && $('gtWork').classList.contains('gt-max')) exitFull(); });
        document.addEventListener('visibilitychange', function () { if (!document.hidden && map) map.invalidateSize(); });
        // theme switch repaints the chart
        try { new MutationObserver(function () { if (chart) chart.redraw(); }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] }); } catch (_) {}
    }

    // ── share link: the track in the URL, nothing on our server ───────────
    function encPoly(lat, lon) {
        var out = '', plat = 0, plon = 0;
        function enc(v) { v = v < 0 ? ~(v << 1) : v << 1; var s = ''; while (v >= 0x20) { s += String.fromCharCode((0x20 | (v & 0x1f)) + 63); v >>= 5; } return s + String.fromCharCode(v + 63); }
        for (var i = 0; i < lat.length; i++) {
            var a = Math.round(lat[i] * 1e5), b = Math.round(lon[i] * 1e5);
            out += enc(a - plat) + enc(b - plon); plat = a; plon = b;
        }
        return out;
    }
    function decPoly(s) {
        var lat = [], lon = [], i = 0, a = 0, b = 0;
        function dec() { var r = 0, sh = 0, c; do { c = s.charCodeAt(i++) - 63; r |= (c & 0x1f) << sh; sh += 5; } while (c >= 0x20); return r & 1 ? ~(r >> 1) : r >> 1; }
        while (i < s.length) { a += dec(); b += dec(); lat.push(a / 1e5); lon.push(b / 1e5); }
        return { lat: lat, lon: lon };
    }
    function shareLink() {
        var tr = doc && doc.tracks[active];
        if (!tr) return;
        var sr = derived(tr).sr;
        var m = G.simplifyTo(sr.lat, sr.lon, 1500).mask;
        var la = [], lo = [];
        for (var i = 0; i < sr.n; i++) if (m[i]) { la.push(sr.lat[i]); lo.push(sr.lon[i]); }
        var payload = 'n=' + encodeURIComponent((tr.name || '').slice(0, 80)) + '&c=' + encodeURIComponent((tr.color || '').replace('#', '')) + '&p=' + encodeURIComponent(encPoly(la, lo));
        var url = location.origin + location.pathname + '#' + payload;
        var copied = function () { toast(t('share_copied'), 'success'); };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(copied, function () { prompt(t('share_copy_manual'), url); });
        else prompt(t('share_copy_manual'), url);
        ae('feature_use', { f: 'share', pts: la.length, len: url.length });
    }
    function readHash() {
        var h = location.hash.replace(/^#/, '');
        if (!/(^|&)p=/.test(h)) return false;
        var q = {};
        h.split('&').forEach(function (kv) { var i = kv.indexOf('='); if (i > 0) q[kv.slice(0, i)] = decodeURIComponent(kv.slice(i + 1)); });
        try {
            var P = decPoly(q.p || '');
            if (P.lat.length < 2) return false;
            var B = new GpsParse._SegB();
            for (var i = 0; i < P.lat.length; i++) B.push(P.lat[i], P.lon[i], null, null, null);
            var d = { format: 'link', name: q.n || null, creator: null, device: null, time: null, tracks: [{ kind: 'track', name: q.n || null, desc: null, sport: null, color: /^[0-9a-f]{6}$/i.test(q.c || '') ? '#' + q.c : null, segs: [B.done()], laps: [], device: null }], waypoints: [], areas: [], warnings: [] };
            tagDoc(d, null);
            d.tracks[0].src = 'link';
            addDocs([d], false);
            ae('feature_use', { f: 'share_open', pts: P.lat.length });
            return true;
        } catch (_) { return false; }
    }

    // ── the track as a video ──────────────────────────────────────────────
    /* A short MP4 of the ride: fly-in, the rider along the line with the
     * numbers, fly-out (video.js draws, CanvasVideoExport encodes — hardware
     * H.264, in this browser). The preview in the dialog is the SAME frame
     * function at the export's own size, scaled down — what plays is what
     * downloads. 1080p sits behind the site's usual export-quality ceiling
     * (data-quality-gate="height": 720p without Premium, visibly closed). The
     * video of a track counts as that track's file for the daily limit — the
     * GPX of the same ride saved today is the same unit. */
    var Vid = { film: null, raf: 0, t0: 0, ac: null, busy: false, loading: 0 };
    var vidAspect = load('vid_aspect', '16:9'), vidDur = +load('vid_dur', '15') || 15, vidStyle = load('vid_style', 'dark');
    function vidSize() {
        var sh = +($('gtVidRes').value || 720) || 720;
        if (vidAspect === '9:16') return [sh, Math.round(sh * 16 / 9)];
        if (vidAspect === '1:1') return [sh, sh];
        return [Math.round(sh * 16 / 9), sh];
    }
    function vidFilm() {
        var tr = doc && doc.tracks[active];
        if (!tr) return null;
        var d = derived(tr), st = d.stats;
        var sub = [tr.sport ? sportLabel(tr.sport) : '', st.start ? fmtDate(st.start, d.sr.lat[0], d.sr.lon[0]) : '', U.dist(st.distance)].filter(function (x) { return x && x !== '…'; }).join(' · ');
        return GpsVideo.create({
            sr: d.sr, speed: d.speed, foot: d.foot, title: tr.name || doc.name || '', sub: sub,
            color: vidStyle === 'light' ? '#e8590c' : '#ff6b2c', style: vidStyle, tiles: CFG.tile, relief: CFG.terrain, units: U, t: t,
            brand: location.host, dur: vidDur
        });
    }
    function vidPaintSegs() {
        [['gtVidAspect', vidAspect], ['gtVidDur', String(vidDur)], ['gtVidStyle', vidStyle]].forEach(function (p) {
            document.querySelectorAll('#' + p[0] + ' [data-v]').forEach(function (b) { var on = b.getAttribute('data-v') === p[1]; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        });
    }
    function vidRebuild() {
        cancelAnimationFrame(Vid.raf);
        Vid.film = vidFilm();
        if (!Vid.film) return;
        var sz = vidSize(), cv = $('gtVidCv');
        // the preview canvas: the export's aspect, at most ~640 px wide
        var k = Math.min(1, 640 / sz[0], 420 / sz[1]);
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        cv.width = Math.round(sz[0] * k * dpr); cv.height = Math.round(sz[1] * k * dpr);
        cv.style.width = Math.round(sz[0] * k) + 'px'; cv.style.height = 'auto';
        var P = Vid.film.plan(sz[0], sz[1]);
        var my = ++Vid.loading, film = Vid.film;
        $('gtVidLoad').hidden = false;
        $('gtVidLoadText').textContent = t('vid_loading', { p: 0 });
        film.load(sz[0], sz[1], function (f) { if (my === Vid.loading) $('gtVidLoadText').textContent = t('vid_loading', { p: Math.round(f * 100) }); }).then(function (failed) {
            if (my !== Vid.loading) return;
            $('gtVidLoad').hidden = true;
            if (failed && failed >= P.tiles * 0.5) toast(t('vid_map_failed'), 'warning');
            Vid.t0 = performance.now();
            var ctx = cv.getContext('2d');
            (function loop() {
                if (!$('gtVid').open || film !== Vid.film) return;
                var tt = ((performance.now() - Vid.t0) / 1000) % (film.duration + 1.2);
                ctx.setTransform(cv.width / sz[0], 0, 0, cv.height / sz[1], 0, 0);
                film.draw(ctx, Math.min(tt, film.duration), sz[0], sz[1]);
                Vid.raf = requestAnimationFrame(loop);
            })();
        });
    }
    function loadScriptOnce(url, test) {
        if (test()) return Promise.resolve();
        return new Promise(function (res, rej) {
            var s = document.createElement('script');
            s.src = url; s.onload = function () { test() ? res() : rej(new Error('load')); }; s.onerror = function () { rej(new Error('load')); };
            document.head.appendChild(s);
        });
    }
    function openVideo() {
        var tr = doc && doc.tracks[active];
        if (!tr || !window.GpsVideo) return;
        var dlg = $('gtVid');
        vidPaintSegs();
        if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
        vidRebuild();
        ae('feature_use', { f: 'video_open', pts: derived(tr).sr.n });
        // the encoder, while the person looks at the preview
        loadScriptOnce(CFG.cveUrl, function () { return !!window.CanvasVideoExport; }).catch(function () {});
    }
    function closeVideo() {
        if (Vid.busy) return;
        cancelAnimationFrame(Vid.raf); Vid.loading++;
        var dlg = $('gtVid'); if (dlg.close) dlg.close(); else dlg.removeAttribute('open');
    }
    function vidGo() {
        if (Vid.busy || !Vid.film) return;
        var tr = doc && doc.tracks[active];
        if (!tr) return;
        var run = window.__toolLimit ? window.__toolLimit.begin(SLUG, [tr.src || 'x']) : { commit: function () {}, abort: function () {} };
        if (!run) return;
        var sz = vidSize(), film = Vid.film, t0 = performance.now();
        Vid.busy = true; Vid.ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
        var prog = function (f) { $('gtVidBar').style.width = Math.round(f * 100) + '%'; $('gtVidProgText').textContent = t('vid_progress', { p: Math.round(f * 100) }); };
        $('gtVidProg').hidden = false; $('gtVidGo').disabled = true; prog(0);
        loadScriptOnce(CFG.cveUrl, function () { return !!window.CanvasVideoExport; }).then(function () {
            return window.CanvasVideoExport.isSupported({ width: sz[0], height: sz[1], fps: 30, format: 'mp4' });
        }).then(function (sup) {
            // no audio in this film: an encoder without its audio half is fine
            if (!sup.ok && sup.reason !== 'no-audio-encoder') { var e = new Error('unsupported:' + sup.reason); e.unsupported = true; throw e; }
            return film.load(sz[0], sz[1]);
        }).then(function () {
            return window.CanvasVideoExport.encode({
                width: sz[0], height: sz[1], fps: 30, durationSec: film.duration, format: 'mp4',
                drawFrame: function (ctx, i, tSec) { film.draw(ctx, tSec, sz[0], sz[1]); },
                onProgress: prog, signal: Vid.ac ? Vid.ac.signal : undefined
            });
        }).then(function (blob) {
            return window.__downloadFile(blob, safeName($('gtFileName').value || tr.name || 'track') + '.mp4');
        }).then(function (ok) {
            if (ok === false) { run.abort(); return; }
            run.commit();
            ae('gt_video', { ok: 1, aspect: vidAspect, h: sz[1], dur: vidDur, style: vidStyle, ms: Math.round(performance.now() - t0) });
            try { if (window.__aeToolComplete) window.__aeToolComplete({ fmt: 'mp4', from: doc.format, tracks: 1, video: 1 }); } catch (_) {}
            toast(t('vid_done'), 'success');
        }, function (e) {
            run.abort();
            var aborted = e && e.name === 'AbortError';
            if (!aborted) toast(e && e.unsupported ? t('vid_unsupported') : t('save_failed'), 'error');
            ae(aborted ? 'feature_use' : 'tool_diagnostic', aborted ? { f: 'video_stop' } : { issue: 'gt_video_failed', err: String(e && e.message || e).slice(0, 80), aspect: vidAspect, h: sz[1] });
        }).then(function () {
            Vid.busy = false; Vid.ac = null;
            $('gtVidProg').hidden = true; $('gtVidGo').disabled = false;
        });
    }
    function bindVideo() {
        if (!$('gtVid')) return;
        $('gtVideo').addEventListener('click', openVideo);
        $('gtVidClose').addEventListener('click', closeVideo);
        $('gtVid').addEventListener('cancel', function (e) { if (Vid.busy) e.preventDefault(); else { cancelAnimationFrame(Vid.raf); Vid.loading++; } });
        $('gtVidGo').addEventListener('click', vidGo);
        $('gtVidStop').addEventListener('click', function () { if (Vid.ac) Vid.ac.abort(); });
        [['gtVidAspect', function (v) { vidAspect = v; save('vid_aspect', v); }], ['gtVidDur', function (v) { vidDur = +v; save('vid_dur', v); }], ['gtVidStyle', function (v) { vidStyle = v; save('vid_style', v); }]].forEach(function (p) {
            document.querySelectorAll('#' + p[0] + ' [data-v]').forEach(function (b) {
                b.addEventListener('click', function () { if (Vid.busy) return; p[1](b.getAttribute('data-v')); vidPaintSegs(); vidRebuild(); });
            });
        });
        $('gtVidRes').addEventListener('change', function () { if (!Vid.busy) vidRebuild(); });
    }

    // ── drawing a route ───────────────────────────────────────────────────
    /* «Make a GPX»: points clicked on the map, joined by straight lines, the
     * elevation of every 25 m taken from the terrain tiles, the time it takes
     * on foot and by bike estimated from the grade. No snapping to roads — a
     * navigator given the file as a ROUTE (GPX route points) lays the road
     * itself, and the page says so. Every point change is an ordinary edit:
     * undo, redo, save and every other tool work on the drawn route as on any
     * track. The route's own points live on the track (`drawn.pts`); a tool
     * that changes the track's segments ends the drawing (the pencil hides). */
    var Draw = (function () {
        var on = false, markers = [], eleCache = {}, demBusy = 0;
        function draft() { var tr = doc && doc.tracks[active]; return tr && tr.drawn && tr.drawn.segs === tr.segs ? tr : null; }
        function segKey(a, b) { return a[0].toFixed(6) + ',' + a[1].toFixed(6) + ';' + b[0].toFixed(6) + ',' + b[1].toFixed(6); }
        function densify(a, b) {
            var d = G.dist(a[0], a[1], b[0], b[1]), m = Math.max(1, Math.min(400, Math.ceil(d / 25))), la = [], lo = [];
            for (var i = 0; i < m; i++) { var f = i / m; la.push(a[0] + (b[0] - a[0]) * f); lo.push(a[1] + (b[1] - a[1]) * f); }
            return { lat: la, lon: lo };
        }
        function build(pts, vIdx) {
            var la = [], lo = [], el = [], anyEle = false;
            for (var i = 0; i + 1 < pts.length; i++) {
                var s = densify(pts[i], pts[i + 1]), ec = eleCache[segKey(pts[i], pts[i + 1])];
                if (vIdx) vIdx.push(la.length);
                for (var k = 0; k < s.lat.length; k++) { la.push(s.lat[k]); lo.push(s.lon[k]); el.push(ec ? ec[k] : NaN); if (ec) anyEle = true; }
            }
            if (vIdx && pts.length) vIdx.push(la.length);
            if (pts.length) {
                var last = pts[pts.length - 1];
                la.push(last[0]); lo.push(last[1]);
                var lc = pts.length > 1 ? eleCache[segKey(pts[pts.length - 2], last)] : null;
                el.push(lc ? lc[lc.length - 1] : NaN);
            }
            var n = la.length;
            return [{ n: n, lat: Float64Array.from(la), lon: Float64Array.from(lo), ele: anyEle ? Float32Array.from(el) : null, time: null, ch: {} }];
        }
        function trackOf(base, pts) {
            var vIdx = [], segs = build(pts, vIdx);
            var tr = {};
            for (var k in base) tr[k] = base[k];
            tr.segs = segs; tr.drawn = { pts: pts, segs: segs, vIdx: vIdx }; tr.kind = 'route'; tr.edited = true; tr.device = null; tr.laps = [];
            return tr;
        }
        function withPts(pts, label) {
            var tr = doc.tracks[active];
            var next = {}; for (var k in doc) next[k] = doc[k];
            next.tracks = doc.tracks.slice(); next.tracks[active] = trackOf(tr, pts);
            if (label) commit(next, label); else { doc = next; render(); }
            fillEle();
        }
        /* terrain heights for the segments that do not have them yet; they
         * replace the draft in place — fetching a height is not an edit */
        function fillEle() {
            var tr = draft();
            if (!tr || !window.GpsDem) return;
            var pts = tr.drawn.pts, need = [];
            for (var i = 0; i + 1 < pts.length; i++) { var k = segKey(pts[i], pts[i + 1]); if (!eleCache[k]) need.push([k, densify(pts[i], pts[i + 1]), pts[i + 1]]); }
            if (!need.length) return;
            var la = [], lo = [];
            need.forEach(function (x) { x[1].lat.forEach(function (v) { la.push(v); }); x[1].lon.forEach(function (v) { lo.push(v); }); la.push(x[2][0]); lo.push(x[2][1]); });
            var my = ++demBusy;
            GpsDem.sample(la, lo, { urls: CFG.terrain, zoom: 13 }).then(function (res) {
                var o = 0;
                need.forEach(function (x) { var m = x[1].lat.length + 1; eleCache[x[0]] = res.ele.slice(o, o + m); o += m; });
                if (my !== demBusy) return;
                var cur = draft();
                if (!cur) return;
                var next = {}; for (var k2 in doc) next[k2] = doc[k2];
                next.tracks = doc.tracks.slice(); next.tracks[active] = trackOf(cur, cur.drawn.pts);
                next.tracks[active].eleSource = 'dem';
                doc = next;
                render();
            }, function () { toast(t('ele_dem_failed'), 'warning'); });
        }
        function paintMarkers() {
            markers.forEach(function (m) { map.removeLayer(m); });
            markers = [];
            var tr = draft();
            if (!on || !tr || !map) return;
            var pts = tr.drawn.pts;
            pts.forEach(function (p, i) {
                var cls = 'gt-vx' + (i === 0 ? ' first' : '') + (i === pts.length - 1 && i > 0 ? ' last' : '');
                var m = L.marker([p[0], p[1]], { draggable: true, keyboard: false, title: t('draw_point_title'), icon: L.divIcon({ className: cls, iconSize: null, html: '' }), zIndexOffset: 1000 });
                m.on('dragend', function () { var ll = m.getLatLng(), np = pts.slice(); np[i] = [ll.lat, ll.lng]; withPts(np, 'draw_move'); });
                m.on('contextmenu dblclick', function (e) { if (e.originalEvent) { L.DomEvent.stop(e.originalEvent); } var np = pts.slice(); np.splice(i, 1); withPts(np, 'draw_remove'); });
                m.on('click', function (e) { if (e.originalEvent) L.DomEvent.stopPropagation(e.originalEvent); });
                m.addTo(map);
                markers.push(m);
            });
        }
        function paint() {
            var tr = draft(), bar = $('gtDrawBar'), pen = $('gtDrawEdit');
            if (!tr) on = false;
            $('gtWork').classList.toggle('gt-drawing', on);
            bar.hidden = !on;
            pen.hidden = on || !tr;
            if (on) {
                var n = tr.drawn.pts.length;
                $('gtDrawTip').textContent = n < 2 ? t('draw_tip') : t('draw_tip_more', { d: U.dist(derived(tr).stats.distance) });
                $('gtDrawLoop').disabled = n < 3;
                if (map) map.doubleClickZoom.disable();
            } else if (map) map.doubleClickZoom.enable();
            applyGestures();
            paintMarkers();
        }
        function onMapClick(e) {
            if (!on) return;
            var tr = draft();
            if (!tr) return;
            var np = tr.drawn.pts.concat([[e.latlng.lat, e.latlng.lng]]);
            withPts(np, np.length === 1 ? null : 'draw_add');
            ae('feature_use', { f: 'draw_point', n: np.length });
        }
        function begin(file) {
            // a new, empty route in the frame; the uploader already shows its file row
            resetAll();
            var key = fileKey(file);
            srcFiles[key] = { name: file.name, size: 0 };
            var tr = { kind: 'route', name: t('draw_name'), desc: null, sport: null, color: PALETTE[0], segs: [], laps: [], device: null, src: key, srcName: file.name };
            tr = trackOf(tr, []);
            var d = { format: 'draw', name: t('draw_name'), creator: null, device: null, time: null, tracks: [tr], waypoints: [], areas: [], warnings: [] };
            commit(d, null, true);
            active = 0;
            on = true;
            showWork();
            if (map) {
                map.once('click', function () {}); // Leaflet binds lazily; nothing to do
                if (!map._gtDrawBound) { map.on('click', onMapClick); map._gtDrawBound = true; }
            }
            setTab('stats');
            paint();
            ae('feature_use', { f: 'draw_start' });
        }
        function bind() {
            $('gtDraw').addEventListener('click', function () {
                warmWorker();
                injectFile(new File(['<gpx version="1.1" creator="draw"></gpx>'], t('draw_file') + '.gpx', { type: 'application/gpx+xml', lastModified: Date.now() }), 'draw');
            });
            $('gtDrawDone').addEventListener('click', function () { on = false; paint(); if (layer) layer.fit([active]); ae('feature_use', { f: 'draw_done', n: (draft() || { drawn: { pts: [] } }).drawn.pts.length }); });
            $('gtDrawEdit').addEventListener('click', function () { if (!draft()) return; on = true; if (map && !map._gtDrawBound) { map.on('click', onMapClick); map._gtDrawBound = true; } paint(); });
            $('gtDrawLoop').addEventListener('click', function () { var tr = draft(); if (!tr || tr.drawn.pts.length < 3) return; withPts(tr.drawn.pts.concat([tr.drawn.pts[0]]), 'draw_loop'); });
            $('gtDrawLocate').addEventListener('click', function () {
                if (!map || !navigator.geolocation) return;
                navigator.geolocation.getCurrentPosition(function (p) { map.setView([p.coords.latitude, p.coords.longitude], Math.max(map.getZoom(), 14)); }, function () { toast(t('draw_locate_failed'), 'warning'); }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 });
            });
        }
        /* A drawn route saved AS A ROUTE carries the points the person placed,
         * not the 25 m samples the profile is built from: a navigator routes
         * between route points, and 2 000 of them would be 2 000 waypoints. */
        function vertices(tr) {
            if (!tr.drawn || tr.drawn.segs !== tr.segs || !tr.drawn.vIdx) return tr;
            var s = tr.segs[0], vi = tr.drawn.vIdx, n = vi.length;
            var seg = { n: n, lat: new Float64Array(n), lon: new Float64Array(n), ele: s.ele ? new Float32Array(n) : null, time: null, ch: {} };
            for (var i = 0; i < n; i++) { var j = Math.min(vi[i], s.n - 1); seg.lat[i] = s.lat[j]; seg.lon[i] = s.lon[j]; if (seg.ele) seg.ele[i] = s.ele[j]; }
            var o = {}; for (var k in tr) o[k] = tr[k];
            o.segs = [seg];
            return o;
        }
        return { begin: begin, paint: paint, bind: bind, vertices: vertices, get on() { return on; } };
    })();

    // ── picture & printable report ────────────────────────────────────────
    function exportPicture(kind) {
        if (!doc || !doc.tracks[active] || !window.GpsImage) return;
        var tr = doc.tracks[active], d = derived(tr);
        var keys = [tr.src || 'x'];
        var run = window.__toolLimit ? window.__toolLimit.begin(SLUG, keys) : { commit: function () {}, abort: function () {} };
        if (!run) return;
        var btn = kind === 'pdf' ? $('gtPdf') : $('gtImage');
        btn.disabled = true;
        var P = panelsFor(tr).all.filter(function (p) { return p.key === 'ele' || p.key === 'hr' || p.key === 'speed' || p.key === 'pace'; });
        window.GpsImage.render({
            kind: kind, track: tr, derived: d, units: U, t: t, skin: 'light', tiles: CFG.tile, panels: P,
            title: tr.name || doc.name || '', brand: (CFG.brand || 'Timbrica') + ' · ' + location.host,
            stats: statLines(tr, d), splits: kind === 'pdf' ? G.splits(d.sr, U.unitLen()) : null, foot: d.foot, hasHr: !!d.sr.ch.hr, jspdfUrl: CFG.jspdfUrl
        }).then(function (blob) {
            return window.__downloadFile(blob, safeName($('gtFileName').value || tr.name || 'track') + (kind === 'pdf' ? '.pdf' : '.png'));
        }).then(function (ok) {
            if (ok === false) run.abort(); else { run.commit(); try { window.__aeToolComplete && window.__aeToolComplete({ fmt: kind, from: doc.format, tracks: 1, pts: d.sr.n }); } catch (_) {} }
        }, function (e) {
            run.abort();
            toast(t('save_failed'), 'error');
            ae('tool_diagnostic', { issue: 'gt_picture_failed', kind: kind, err: String(e && e.message || e).slice(0, 80) });
        }).then(function () { btn.disabled = false; });
    }
    function statLines(tr, d) {
        var st = d.stats, out = [];
        out.push([t('s_dist'), U.dist(st.distance)]);
        if (st.moving != null) out.push([t('s_moving'), U.dur(st.moving)]);
        var gn = gainOf(tr, d);
        if (gn.gain != null) out.push([t('s_gain'), '↑ ' + U.ele(gn.gain) + (gn.loss != null ? '  ↓ ' + U.ele(gn.loss) : '')]);
        if (st.moving) out.push([d.foot ? t('s_pace') : t('s_avg_speed'), d.foot ? U.pace(st.movingSpeed) : U.speed(st.movingSpeed)]);
        if (st.climb && st.climb.max != null) out.push([t('s_ele_range'), U.ele(st.climb.min) + ' – ' + U.ele(st.climb.max)]);
        if (st.hr) out.push([t('s_hr'), NF0.format(st.hr.avg) + ' / ' + NF0.format(st.hr.max) + ' ' + t('u_bpm')]);
        if (st.power) out.push([t('s_power'), NF0.format(st.power.avg) + ' ' + t('u_w')]);
        if (st.start) out.push([t('s_start'), fmtDate(st.start, d.sr.lat[0], d.sr.lon[0])]);
        return out;
    }

    function init() {
        if (!$('gtApp')) return;
        bindUploader();
        bindUi();
        bindTabs();
        bindResize();
        bindLayers();
        bindGestures();
        bindVideo();
        Draw.bind();
        bindEdits();
        if (profH) $('gtWork').style.setProperty('--gt-prof-h', profH + 'px');
        if (location.hash && readHash()) return;
        Recent.render();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

    window.__gpxViewer = {
        get doc() { return doc; }, openFiles: openFiles, derived: derived, get active() { return active; },
        climbs: function () { return doc && doc.tracks[active] ? climbsOf(doc.tracks[active]) : []; },
        setTab: setTab, selectRange: selectRange,
        _pending: function () { return Object.keys(pending).length; }, _worker: function () { return !!worker; }
    };
})();
